import React, { useCallback, useEffect, useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/DatePicker.css';
import { marked } from 'marked';


import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Select,
    MenuItem,
    Chip,
    Button,
    IconButton,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    TextField,
    FormControl,
    InputLabel,
    CircularProgress,
    Autocomplete,
    Checkbox,
    ListItemText,
    Menu,
    Divider,
    TableSortLabel,
} from '@mui/material';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    Clear as ClearIcon,
    Save as SaveIcon,
    LocationOn as LocationOnIcon,
    FileDownload as FileDownloadIcon,
    PictureAsPdf as PictureAsPdfIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    UnfoldMore as UnfoldMoreIcon,
    ViewColumn as ViewColumnIcon,
    VisibilityOff as VisibilityOffIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TableVirtuoso, GroupedTableVirtuoso } from 'react-virtuoso';
import ObjectDialog from './ObjectsReestrView/ObjectDialog.jsx';
import AuditHistoryDialog from './common/AuditHistoryDialog.jsx';
import EquipmentTextEditDialog from './ObjectsReestrView/EquipmentTextEditDialog.jsx';
import { useSelector, useDispatch } from 'react-redux';
import { fetchState, toggleDarkMode } from '../../store/reducers/stateReducer.js';
import { $authHost } from '../../http/index.js';
import { ktpIcon, projectStatusOreolIcon } from '../layers/icons.js';
import '../../utils/arial-normal.js'
import '../../utils/arial-bold.js'
import { values } from 'lodash';
import { AdvancedFiltersComponent } from './common/AdvancedFilters.jsx';
import { selectMappedElectricity } from '../../store/selectors/supervisionSelector.js'
// Регистрируем русскую локаль
registerLocale('ru', ru);



function formatRuble(str) {
    return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
    }).format(str || 0)
}

function formatDateShort(str) {
    return formatDate(str, true)
}

function formatDate(str, short = false) {
    if (!str) return '';

    // Если дата в формате "YYYY-MM-DD" (из базы данных)
    if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [, year, month, day] = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата уже в формате "DD-MM-YYYY"
    if (str.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [, day, month, year] = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата в формате "HH:MM DD-MM-YYYY"
    if (str.match(/^\d{2}:\d{2}\s\d{2}-\d{2}-\d{4}$/)) {
        const [, hours, minutes, day, month, year] = str.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        return `${hours}:${minutes} ${day}.${month}.${year}`;
    }

    // Попытка парсинга как обычной даты
    try {
        const date = new Date(str);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return short ? `${day}.${month}.${year}` : `${hours}:${minutes} ${day}.${month}.${year}`;
    } catch {
        return '';
    }
}

// Компонент для отображения markdown контента
const MarkdownContent = ({ content, darkMode }) => {
    if (!content) return null;

    const htmlContent = marked(content);

    return (
        <Box
            sx={{
                '& p': {
                    margin: '1px 0',
                    fontSize: '0.75rem',
                    lineHeight: '1.2',
                },
                '& strong': {
                    fontWeight: 'bold',
                },
                '& em': {
                    fontStyle: 'italic',
                },
                '& ul, & ol': {
                    margin: '1px 0',
                    paddingLeft: '8px',
                },
                '& li': {
                    margin: '0px 0',
                    fontSize: '0.75rem',
                },
                '& code': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    padding: '1px 2px',
                    borderRadius: '2px',
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                },
                '& pre': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    padding: '2px',
                    borderRadius: '2px',
                    overflow: 'auto',
                    fontSize: '0.7rem',
                },
                '& blockquote': {
                    borderLeft: `2px solid ${darkMode ? '#2196f3' : '#1565c0'}`,
                    paddingLeft: '4px',
                    margin: '1px 0',
                    fontStyle: 'italic',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '1px 0',
                    fontWeight: 'bold',
                },
                '& h1': { fontSize: '0.9rem' },
                '& h2': { fontSize: '0.85rem' },
                '& h3': { fontSize: '0.8rem' },
                '& h4': { fontSize: '0.75rem' },
                '& h5': { fontSize: '0.7rem' },
                '& h6': { fontSize: '0.65rem' },
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    );
};

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        // Если дата уже в формате "YYYY-MM-DD" (из базы данных)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
        }

        // Если дата в формате "HH:MM DD-MM-YYYY"
        const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        if (dateTimeMatch) {
            const [, hours, minutes, day, month, year] = dateTimeMatch;
            return `${year}-${month}-${day}`;
        }

        // Если дата в формате "DD-MM-YYYY"  
        const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dateMatch) {
            const [, day, month, year] = dateMatch;
            return `${year}-${month}-${day}`;
        }

        // Попытка парсинга как обычной даты
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

// Функция для форматирования даты из input в формат "YYYY-MM-DD" для базы данных
const formatDateFromInput = (inputValue) => {
    if (!inputValue) return '';
    try {
        const date = new Date(inputValue);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const columns = [
    {
        id: 'fid', label: '№', minWidth: 30, width: 30, maxWidth: 30, // Немного больше для лучшей читаемости
        align: 'center',
    },
    {
        id: 'name', label: 'Наименование', minWidth: 150, width: 150, maxWidth: 150,// Больше места для названий
        align: 'center', // Лучше выравнивание для текста
    },
    {
        id: 'name_by_acts', label: 'Наименование по актам', minWidth: 150, width: 150, maxWidth: 150, // Больше для текста
        align: 'center',
    },
    {
        id: 'start',
        label: 'Начало строительства',
        minWidth: 160, width: 160, maxWidth: 160, // По 120px на каждую подколонку
        colSpan: 2,
        align: 'center',
        tooltip: 'Плановое начало | Фактическое начало',
    },
    {
        id: 'end',
        label: 'Завершение строительства',
        minWidth: 160, width: 160, maxWidth: 160, // По 120px на каждую подколонку
        colSpan: 2,
        align: 'center',
        tooltip: 'Плановое завершение | Фактическое завершение',
    },
    {
        id: 'stage', label: 'Этап', minWidth: 60, width: 60, maxWidth: 60, // Немного больше для цифр
        align: 'center',
    },
    {
        id: 'closing', label: 'Закрытие без НДС', minWidth: 100, width: 100, maxWidth: 100, // Немного больше для цифр
        align: 'center',
    },
    {
        id: 'cost', label: 'Стоимость без НДС', minWidth: 100, width: 100, maxWidth: 100, // Немного больше для цифр
        align: 'center',
    },
    {
        id: 'comment', label: 'Комментарий', minWidth: 200, width: 200, maxWidth: 200,// Больше для текста
        align: 'center',
    },
    {
        id: 'responsible', label: 'Ответственный', minWidth: 120, width: 120, maxWidth: 120, // Больше для имен
        align: 'center',
    },
    {
        id: 'coexecutors', label: 'Соисполнители', minWidth: 120, width: 120, maxWidth: 120, // Больше для имен
        align: 'center',
    },
    {
        id: 'equipment', label: 'Оснащение', minWidth: 200, width: 200, maxWidth: 200, // Больше для названий
        align: 'center',
    },
    {
        id: 'created', label: 'Создано', minWidth: 120, width: 120, maxWidth: 120, // Больше для дат и имен
        align: 'center',
    },
    {
        id: 'modified', label: 'Изменено', minWidth: 120, width: 120, maxWidth: 120, // Больше для дат и имен
        align: 'center',
    },
    // { id: 'actions', label: 'Действия', minWidth: 80, align: 'center' },
];


const VirtuosoTableComponents = {

    Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            width: '100%',
            minWidth: '100%',
            overflowX: 'auto', // для скролла
        }} />
    )),
    Table: (props) => (
        <Table {...props} sx={{
            borderCollapse: 'separate',
            tableLayout: 'fixed', // Автоматическое распределение ширины
            bgcolor: 'transparent',
            width: 'fit-content',
            // width: '100%', // Используем всю доступную ширину
            // minWidth: '100%', // Минимальная ширина 100%
        }} />
    ),
    TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

function fixedHeaderContent(darkMode, editMode, selectedRows, filteredData, handleSelectAll, sortBy, sortDirection, handleSort, visibleColumns, columnWidths, getColumnWidth, handleResizeStart) {

    // Функция для получения иконки сортировки
    const getSortIcon = (columnId) => {
        if (sortBy !== columnId) {
            return null;
        }
        return sortDirection === 'asc'
            ? <ArrowUpwardIcon fontSize="small" />
            : <ArrowDownwardIcon fontSize="small" />;
    };

    // Получаем только видимые столбцы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);

    return (
        <>
            <TableRow>
                {visibleColumnsArray.map((column, index) => {
                    const columnWidth = getColumnWidth(column.id);
                    return (
                        <TableCell
                            key={column.id}
                            variant="head"
                            align={column.align || 'left'}
                            style={{
                                width: columnWidth,
                                minWidth: column.minWidth,
                                maxWidth: 'none',
                                position: 'relative'
                            }}
                            colSpan={column.colSpan}
                            sx={{
                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                color: darkMode ? '#f5f7fa' : '#333',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                lineHeight: '1.2',
                                borderBottom: darkMode
                                    ? '2px solid rgba(255, 255, 255, 0.12)'
                                    : '2px solid rgba(0, 0, 0, 0.12)',
                                borderRight: darkMode
                                    ? '1px solid rgba(255, 255, 255, 0.08)'
                                    : '1px solid rgba(0, 0, 0, 0.08)',
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                p: '2px 8px',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                },
                                // Принудительно переопределяем цвета для всех дочерних элементов
                                '& .MuiTableSortLabel-root': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '& .MuiTableSortLabel-root:hover': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                '& .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                            }}
                            onClick={(e) => {
                                // Не вызываем handleSort, если клик был на разделителе
                                if (e.target.closest('.column-resizer')) {
                                    return;
                                }
                                handleSort(column.id);
                            }}
                        >
                            <TableSortLabel
                                active={sortBy === column.id}
                                direction={sortBy === column.id ? sortDirection : 'asc'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSort(column.id);
                                }}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    '&:hover': {
                                        color: darkMode ? '#ffffff' : '#1976d2',
                                    },
                                    '&.MuiTableSortLabel-active': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                    },
                                    '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&:hover .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#ffffff' : '#1976d2',
                                    },
                                    // Принудительно переопределяем цвета для всех состояний
                                    '&.MuiTableSortLabel-root': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                    },
                                    '&.MuiTableSortLabel-root.MuiTableSortLabel-active': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&.MuiTableSortLabel-root:hover': {
                                        color: darkMode ? '#ffffff' : '#1976d2',
                                    },
                                    // Стили для иконки во всех состояниях
                                    '& .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                    },
                                    '& .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                    },
                                    '&.MuiTableSortLabel-active .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&.MuiTableSortLabel-active .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&:hover .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#ffffff' : '#1976d2',
                                    },
                                    '&:hover .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#ffffff' : '#1976d2',
                                    },
                                    // Принудительно переопределяем все возможные цвета
                                    '& *': {
                                        color: 'inherit !important',
                                    },
                                }}
                            >

                                {index === 0 && editMode ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                        <Checkbox
                                            checked={selectedRows.size > 0 && selectedRows.size === filteredData.length}
                                            indeterminate={selectedRows.size > 0 && selectedRows.size < filteredData.length}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            size="small"
                                            sx={{
                                                color: darkMode ? '#64b5f6' : '#1976d2',
                                                p: '2px',
                                                '&.Mui-checked': {
                                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                                            {column.label}
                                        </Typography>
                                        {/* {getSortIcon(column.id)} */}
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        {column.tooltip ? (
                                            <Tooltip title={column.tooltip}>
                                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                                                    {column.label}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                                                {column.label}
                                            </Typography>
                                        )}
                                        {/* {getSortIcon(column.id)} */}
                                    </Box>
                                )}
                            </TableSortLabel>
                            {/* Разделитель для изменения размера */}
                            <Box
                                className="column-resizer"
                                onMouseDown={(e) => handleResizeStart(e, column.id)}
                                sx={{
                                    position: 'absolute',
                                    right: '-2px',
                                    top: 0,
                                    bottom: 0,
                                    width: '4px',
                                    cursor: 'col-resize',
                                    zIndex: 11,
                                    backgroundColor: 'transparent',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.6)' : 'rgba(25, 118, 210, 0.4)',
                                    },
                                }}
                            />
                        </TableCell>
                    );
                })}
            </TableRow>
            {/* Второй ряд заголовков для подколонок дат */}
            {/* <TableRow>
        <TableCell sx={{
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}></TableCell>
        <TableCell sx={{
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}></TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#64b5f6' : '#1976d2',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>План</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#4caf50' : '#2e7d32',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>Факт</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#64b5f6' : '#1976d2',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>План</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#4caf50' : '#2e7d32',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>Факт</TableCell>
        {[...Array(6)].map((_, index) => (
          <TableCell key={index} sx={{
            bgcolor: darkMode ? '#2b2b2b' : '#fff',
            borderBottom: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)' 
              : '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 57,
            zIndex: 10,
          }}></TableCell>
        ))}
      </TableRow> */}
        </>
    );
}

// Функция для отображения заголовка группы КДП
function groupContent(index, groupedData, darkMode, user, handleOpenCreateDialog, projectsList, visibleColumns) {
    if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
        return null;
    }
    // Находим первый элемент группы для получения названия КДП
    const firstItemInGroup = groupedData.groupedData.find(item => item.group === index);
    const groupName = firstItemInGroup?.groupName || `Группа ${index}`;
    const groupCount = groupedData.groupCountsRows[index];

    // Находим KDP ID для этой группы
    const itemKdpId = firstItemInGroup?.project_id;
    // Если KDP ID не найден в объекте, ищем по названию группы в списке KDP
    const groupKdpId = itemKdpId || projectsList.find(p => p.name === groupName)?.id;

    const groupCost = groupedData.groupedData.filter(item => item.group === index && item.equipmentIndex === 0).reduce((a, v) => a + (Array.isArray(v.cost?.v) ? v.cost.v.slice(0, v.equipment.v.length).reduce((b, w) => b + Number(w), 0) : (Number(v.cost?.v) || 0)), 0)
    const groupClosing = groupedData.groupedData.filter(item => item.group === index && item.equipmentIndex === 0).reduce((a, v) => a + (Array.isArray(v.closing?.v) ? v.closing.v.slice(0, v.equipment.v.length).reduce((b, w) => b + Number(w), 0) : (Number(v.closing?.v) || 0)), 0)

    return (
        <td colSpan={columns.filter(col => visibleColumns[col.id]).reduce((a, v) => a + (v.colSpan || 1), 0)} style={{
            backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0',
            color: darkMode ? '#64b5f6' : '#1976d2',
            fontWeight: 600,
            fontSize: '0.9rem',
            padding: '2px 16px',
            borderBottom: darkMode
                ? '2px solid rgba(255, 255, 255, 0.12)'
                : '2px solid rgba(0, 0, 0, 0.12)',
            borderTop: darkMode
                ? '2px solid rgba(255, 255, 255, 0.12)'
                : '2px solid rgba(0, 0, 0, 0.12)',
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Кнопка создания объекта в группе */}
                    {user.roles?.includes('project_manager') && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCreateDialog(groupKdpId)}
                            sx={{
                                bgcolor: '#4caf50',
                                color: 'white',
                                px: 1.5,
                                py: 0.2,
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                borderRadius: 1.5,
                                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                '&:hover': {
                                    bgcolor: '#388e3c',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            Добавить
                        </Button>
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Проект: {groupName}
                    </Typography>
                </Box>

                {(visibleColumns['closing'] || visibleColumns['cost']) && (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>

                        {visibleColumns['closing'] &&
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip
                                    label={`Закрытие: ${new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(groupClosing || 0)}`}
                                    size="small"
                                    onClick={() => { }}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                                        color: darkMode ? '#ff8f00' : '#e65100',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        border: `1px solid ${darkMode ? '#ff8f00' : '#e65100'}30`,
                                        height: 'auto',
                                        minHeight: '24px',
                                        maxWidth: '100%',
                                        cursor: 'auto',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255, 143, 0, 0.3)' : 'rgba(230, 81, 0, 0.2)',
                                            transform: 'scale(1.02)',
                                        },
                                        transition: 'all 0.2s ease',
                                        '& .MuiChip-label': {
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            textAlign: 'center',
                                            lineHeight: '1.2',
                                            padding: '4px 8px',
                                        },
                                    }}
                                />



                            </Box>
                        }

                        {visibleColumns['cost'] &&
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip
                                    label={`Стоимость: ${new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(groupCost || 0)}`}
                                    size="small"
                                    onClick={() => { }}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                                        color: darkMode ? '#e91e63' : '#c2185b',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        border: `1px solid ${darkMode ? '#e91e63' : '#c2185b'}30`,
                                        height: 'auto',
                                        minHeight: '24px',
                                        maxWidth: '100%',
                                        cursor: 'auto',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(233, 30, 99, 0.3)' : 'rgba(194, 24, 91, 0.2)',
                                            transform: 'scale(1.02)',
                                        },
                                        transition: 'all 0.2s ease',
                                        '& .MuiChip-label': {
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            textAlign: 'center',
                                            lineHeight: '1.2',
                                            padding: '4px 8px',
                                        },
                                    }}
                                />



                            </Box>
                        }

                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Chip с количеством объектов */}
                    <Chip
                        label={`${groupCount}`}
                        size="small"
                        onClick={() => { }}
                        sx={{
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            color: darkMode ? '#64b5f6' : '#1976d2',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                        }}
                    />


                </Box>
            </Box>
        </td>
    );
}

const fieldColors = {
    name: { light: '#2e7d32', dark: '#4caf50' },
    dates: { light: '#1976d2', dark: '#64b5f6' },
    stage: { light: '#6a1b9a', dark: '#ab47bc' },
    responsible: { light: '#d32f2f', dark: '#f44336' },
    coexecutors: { light: '#f57c00', dark: '#ffb74d' },
    equipment: { light: '#3949ab', dark: '#5c6bc0' },
    closing: { light: '#e65100', dark: '#ff8f00' },
    cost: { light: '#c2185b', dark: '#e91e63' },
    closing: { light: '#e65100', dark: '#ff8f00' },
    comment: { light: '#1565c0', dark: '#2196f3' },
    name_by_acts: { light: '#1565c0', dark: '#2196f3' },
};

// Функция для генерации цвета чипа этапа в зависимости от номера этапа
const getStageColor = (stageNumber, darkMode) => {
    // if (!stageNumber || stageNumber === '') return null;

    const num = parseInt(stageNumber || 0);
    if (isNaN(num)) return null;

    // Базовые цвета (синий как основа для лучшей генерации оттенков)
    const baseColors = {
        light: { bg: 'rgba(33, 150, 243, 0.1)', text: '#1976d2', hover: 'rgba(33, 150, 243, 0.2)' },
        dark: { bg: 'rgba(100, 181, 246, 0.2)', text: '#64b5f6', hover: 'rgba(100, 181, 246, 0.3)' }
    };

    const colors = darkMode ? baseColors.dark : baseColors.light;

    // Плавный переход по цветовой палитре с меньшим шагом
    const hueShift = ((num - 1) * 20) % 360; // шаг 20° для плавного перехода
    const saturationModifier = 0.8 + (num % 2) * 0.1; // небольшое изменение насыщенности
    const lightnessModifier = 0.85 + (num % 2) * 0.1; // небольшое изменение яркости

    // Конвертируем hex в HSL для модификации
    const hexToHsl = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }

        return { h: h * 360, s: s * 100, l: l * 100 };
    };

    const hslToRgb = (h, s, l) => {
        h /= 360;
        s /= 100;
        l /= 100;

        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    };

    // Получаем HSL базового цвета текста
    const baseHsl = hexToHsl(colors.text);

    // Применяем модификации с более сильными изменениями
    const modifiedH = (baseHsl.h + hueShift + 360) % 360;
    const modifiedS = Math.min(100, Math.max(20, baseHsl.s * saturationModifier)); // ограничиваем от 20% до 100%
    const modifiedL = Math.min(90, Math.max(30, baseHsl.l * lightnessModifier)); // ограничиваем от 30% до 90%

    // Конвертируем обратно в RGB
    const modifiedRgb = hslToRgb(modifiedH, modifiedS, modifiedL);

    // Генерируем цвета фона с прозрачностью
    const bgAlpha = darkMode ? 0.25 : 0.15; // увеличиваем прозрачность для лучшей видимости
    const hoverAlpha = darkMode ? 0.35 : 0.25;

    return {
        bgcolor: `rgba(${modifiedRgb.r}, ${modifiedRgb.g}, ${modifiedRgb.b}, ${bgAlpha})`,
        color: `rgb(${modifiedRgb.r}, ${modifiedRgb.g}, ${modifiedRgb.b})`,
        hoverBgcolor: `rgba(${modifiedRgb.r}, ${modifiedRgb.g}, ${modifiedRgb.b}, ${hoverAlpha})`
    };
};

const EditableSelectCell = ({ id, field, value, label, saveEditedField, darkMode, options, multiple = false, equipment }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'responsible':
                return {
                    bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                    color: darkMode ? '#f44336' : '#d32f2f',
                    selectedBg: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                };
            case 'coexecutors':
                return {
                    bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                    color: darkMode ? '#ffb74d' : '#f57c00',
                    selectedBg: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                };
            case 'equipment':
                return {
                    bgcolor: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                    color: darkMode ? '#5c6bc0' : '#3949ab',
                    selectedBg: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                };
            case 'stage':
                return {
                    bgcolor: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                    color: darkMode ? '#ab47bc' : '#6a1b9a',
                    selectedBg: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                };
            case 'closing':
                return {
                    bgcolor: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                    color: darkMode ? '#ff8f00' : '#e65100',
                    selectedBg: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                };
            case 'cost':
                return {
                    bgcolor: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                    color: darkMode ? '#e91e63' : '#c2185b',
                    selectedBg: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                };
            case 'closing':
                return {
                    bgcolor: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                    color: darkMode ? '#ff8f00' : '#e65100',
                    selectedBg: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                };
            case 'comment':
            case 'name_by_acts':
                return {
                    bgcolor: darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(21, 101, 192, 0.1)',
                    color: darkMode ? '#2196f3' : '#1565c0',
                    selectedBg: darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(21, 101, 192, 0.1)',
                };
            default:
                return {
                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                    color: darkMode ? '#ce93d8' : '#9c27b0',
                    selectedBg: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value || (multiple ? [] : null));
    const [inputValue, setInputValue] = useState('');

    const handleChange = (event, newValue) => {
        console.log(event, newValue)
        setLocalValue(newValue);
    };

    const saveEditedEquipment = useCallback((newValue, deleted = null) => {
        saveEditedField({
            rowId: id,
            field: field,
            value: newValue,
            isValueChanged: JSON.stringify(value) !== JSON.stringify(newValue),
            shouldExitEditMode: false,
            deleted: deleted
        });

    }, [])


    const handleInputChange = (event, newInputValue) => {
        console.log(newInputValue)
        setInputValue(newInputValue);
    };

    const handleBlur = () => {

        saveEditedField({
            rowId: id,
            field: field,
            value: multiple ? localValue.map(v => v?.id || v || null) : (localValue?.id || localValue || null),
            isValueChanged: JSON.stringify(value) !== JSON.stringify(localValue),
            shouldExitEditMode: false,
            equipment: equipment,
        });


    };

    const handleClear = () => {
        setLocalValue(multiple ? [] : null);
    };

    const handleSave = () => {
        handleBlur();
    };

    const getOptionLabel = (option) => {
        return option?.name || option?.display_name || String(option || '');
    };

    const isOptionEqualToValue = (option, value) => {
        return option?.id === value?.id;
    };

    return (
        <Autocomplete
            value={localValue}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            options={options || []}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            multiple={multiple}

            size="small"
            autoFocus
            freeSolo
            disableClearable={true}
            // clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            disableCloseOnSelect={multiple}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={label}
                    size="small"
                    rows={multiple ? 3 : 1}
                    // InputProps={{
                    //     ...params.InputProps,
                    //     endAdornment: (
                    //         <Box sx={{
                    //             position: multiple ? 'absolute' : '',

                    //             bottom: '5px',
                    //             right: '10px',
                    //             display: 'flex',
                    //             alignItems: 'center',
                    //             gap: 0.5
                    //         }}>
                    //             {multiple && localValue.length > 0 && (
                    //                 <IconButton
                    //                     size="small"
                    //                     onClick={(e) => {
                    //                         e.stopPropagation();
                    //                         handleClear();
                    //                     }}
                    //                     edge="end"
                    //                     tabIndex={-1}
                    //                     sx={{
                    //                         color: darkMode ? '#f44336' : '#d32f2f',
                    //                         p: 0.3,
                    //                         '&:hover': {
                    //                             bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                    //                         },
                    //                     }}
                    //                 >
                    //                     <ClearIcon fontSize="small" />
                    //                 </IconButton>
                    //             )}
                    //             <IconButton
                    //                 size="small"
                    //                 onClick={(e) => {
                    //                     e.stopPropagation();
                    //                     handleSave();
                    //                 }}
                    //                 edge="end"
                    //                 tabIndex={-1}
                    //                 sx={{
                    //                     color: darkMode ? '#4caf50' : '#2e7d32',
                    //                     p: 0.3,
                    //                     '&:hover': {
                    //                         bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                    //                     }
                    //                 }}
                    //             >
                    //                 <SaveIcon fontSize="small" />
                    //             </IconButton>
                    //         </Box>
                    //     ),
                    // }}
                    sx={{
                        '& .MuiOutlinedInput-input': {

                            cursor: 'text',
                            color: darkMode ? '#ffffff' : '#333',
                            fontSize: '0.875rem',
                            width: 'fit-content !important',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldColors.color,
                            borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                        },
                        '& .MuiInputLabel-root': {
                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                            fontSize: '0.875rem',
                            '&.Mui-focused': {
                                color: fieldColors.color,
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                            borderRadius: '4px',
                        },
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        '& .MuiInputBase-root': {
                            p: '5px !important',
                            borderRadius: '4px',
                        },
                        '& .MuiInputBase-input': {

                            fontWeight: 500,
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                        },
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Typography variant="body2">
                        {getOptionLabel(option)}
                    </Typography>
                </Box>
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getOptionLabel(option)}
                        onClick={() => { }}
                        size="small"
                        sx={{
                            bgcolor: fieldColors.bgcolor,
                            color: fieldColors.color,
                            fontSize: '0.7rem',
                            height: 'auto',
                            minHeight: '20px',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: '1.2',
                                padding: '2px 6px',
                            },
                        }}
                    />
                ))
            }
            sx={{
                '& .MuiAutocomplete-popper': {
                    zIndex: 1300,
                },
                '& .MuiAutocomplete-paper': {
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                    '& .MuiAutocomplete-option': {
                        color: darkMode ? '#f5f7fa' : '#333',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                        },
                        '&[aria-selected="true"]': {
                            bgcolor: fieldColors.selectedBg,
                        },
                    },
                },
            }}
        />
    );
};

const EditableNumberCell = ({ id, field, value, label, saveEditedField, darkMode, equipment }) => {
    console.log(id, field, value, label, saveEditedField, darkMode, equipment)
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'stage':
                return {
                    color: darkMode ? '#ab47bc' : '#6a1b9a',
                };
            case 'closing':
                return {
                    color: darkMode ? '#ff8f00' : '#e65100',
                };
            case 'cost':
                return {
                    color: darkMode ? '#e91e63' : '#c2185b',
                };
            case 'fid':
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
            default:
                return {
                    color: darkMode ? '#9c27b0' : '#9c27b0',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    // Инициализируем localValue с учетом типа поля
    const [localValue, setLocalValue] = useState(() => {
        if (field === 'closing' || field === 'cost') {
            // Для вещественных чисел отображаем с двумя знаками после запятой
            return value !== null && value !== undefined ? parseFloat(value).toFixed(2) : '';
        }
        return value || '';
    });

    const handleNumberChange = (e) => {
        const inputValue = e.target.value;

        // Для полей closing и cost разрешаем вещественные числа с двумя знаками после запятой
        if (field === 'closing' || field === 'cost') {
            // Разрешаем цифры, точку, запятую и пустую строку
            // Также разрешаем формат с двумя знаками после запятой
            if (inputValue === '' || /^[\d.,]+$/.test(inputValue)) {
                // Заменяем запятую на точку для корректной обработки
                const normalizedValue = inputValue.replace(',', '.');
                // Проверяем, что после точки не более двух цифр и нет множественных точек
                if (normalizedValue === '' || /^\d*\.?\d{0,2}$/.test(normalizedValue)) {
                    // Дополнительная проверка на множественные точки
                    const dotCount = (normalizedValue.match(/\./g) || []).length;
                    if (dotCount <= 1) {
                        setLocalValue(inputValue);
                    }
                }
            }
        } else {
            // Для остальных полей оставляем только целые числа
            if (inputValue === '' || /^\d+$/.test(inputValue)) {
                setLocalValue(inputValue);
            }
        }
    };

    const handleBlur = () => {
        console.log(value, localValue)
        let numericValue;

        if (field === 'closing' || field === 'cost') {
            // Для вещественных чисел парсим как float
            numericValue = localValue ? parseFloat(localValue.replace(',', '.')) : value;
            // Округляем до двух знаков после запятой
            numericValue = isNaN(numericValue) ? value : Math.round(numericValue * 100) / 100;

            // Если значение некорректное, восстанавливаем исходное
            if (isNaN(numericValue)) {
                setLocalValue(formatDisplayValue(value));
                return;
            }
        } else {
            // Для остальных полей парсим как целое число
            numericValue = localValue ? parseInt(localValue, 10) : value;

            // Если значение некорректное, восстанавливаем исходное
            if (isNaN(numericValue)) {
                setLocalValue(value || '');
                return;
            }
        }


        saveEditedField({
            rowId: id,
            field: field,
            value: numericValue,
            isValueChanged: (value || '') !== localValue,
            equipment: equipment,
        });


    };

    const handleClear = () => {
        setLocalValue('');
    };

    // Функция для форматирования значения при отображении
    const formatDisplayValue = (val) => {
        if (field === 'closing' || field === 'cost') {
            // Для вещественных чисел форматируем с двумя знаками после запятой
            return val !== null && val !== undefined ? parseFloat(val).toFixed(2) : '';
        }
        return val || '';
    };

    const handleSave = () => {
        handleBlur();
    };

    // Обработка нажатия Enter
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleBlur();
        }
    };

    return (
        <TextField
            fullWidth
            size="small"
            value={localValue}
            onChange={handleNumberChange}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            // placeholder={field === 'closing' || field === 'cost' ? '0.00' : label}
            autoFocus
            // InputProps={{
            //     endAdornment: (
            //         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            //             {localValue && (
            //                 <IconButton
            //                     size="small"
            //                     onClick={(e) => {
            //                         e.stopPropagation();
            //                         handleClear();
            //                     }}
            //                     edge="end"
            //                     tabIndex={-1}
            //                     sx={{
            //                         color: darkMode ? '#f44336' : '#d32f2f',
            //                         p: 0.1,
            //                         '&:hover': {
            //                             bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
            //                         },
            //                     }}
            //                 >
            //                     <ClearIcon fontSize="small" />
            //                 </IconButton>
            //             )}
            //             <IconButton
            //                 size="small"
            //                 onClick={(e) => {
            //                     e.stopPropagation();
            //                     handleSave();
            //                 }}
            //                 edge="end"
            //                 tabIndex={-1}
            //                 sx={{
            //                     color: darkMode ? '#4caf50' : '#2e7d32',
            //                     p: 0.1,
            //                     '&:hover': {
            //                         bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
            //                     }
            //                 }}
            //             >
            //                 <SaveIcon fontSize="small" />
            //             </IconButton>
            //         </Box>
            //     ),
            // }}
            sx={{
                '& .MuiOutlinedInput-input': {
                    p: '5px',
                    cursor: 'text',
                    color: darkMode ? '#ffffff' : '#333',
                    fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldColors.color,
                    borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                },
                '& .MuiInputLabel-root': {
                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                    fontSize: '0.875rem',
                    '&.Mui-focused': {
                        color: fieldColors.color,
                    }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                },
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                '& .MuiInputBase-root': {
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    borderRadius: '4px',
                    minWidth: '60px',
                    // width: '100%'
                },
                '& .MuiInputBase-input': {
                    borderRadius: '4px',
                },
            }}
        />
    );
};

const EditableTextAreaCell = ({ id, field, value, label, saveEditedField, darkMode, focusIndex }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'comment':
            case 'name_by_acts':
                return {
                    color: darkMode ? '#2196f3' : '#1565c0',
                };
            case 'closing':
                return {
                    color: darkMode ? '#ff8f00' : '#e65100',
                };
            case 'cost':
                return {
                    color: darkMode ? '#e91e63' : '#c2185b',
                };
            default:
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value);

    const handleTextChange = (e, index) => {
        if (Array.isArray(localValue)) {
            const newArray = [...localValue];
            newArray[index] = e.target.value;

            setLocalValue(newArray);
        } else {
            setLocalValue(e.target.value);
        }
    };

    const handleBlur = () => {
        console.log(value, localValue)
        saveEditedField({
            rowId: id,
            field: field,
            value: localValue,
            isValueChanged: value !== localValue,
            shouldExitEditMode: false // Не выходим из режима редактирования при blur
        });
    };

    return (
        <Box sx={{ display: 'grid', gridAutoRows: '1fr', flex: 1, alignItems: 'center', gap: 0.5, height: '100%' }}>
            {(Array.isArray(localValue) ? localValue : [localValue]).map((value, index) => (
                <TextField
                    multiline
                    rows={10}
                    size="small"
                    value={value || ''}
                    onChange={(e) => handleTextChange(e, index)}
                    onBlur={handleBlur}
                    autoFocus
                    data-editing="true"
                    placeholder={field === 'comment' || field === 'name_by_acts' ? 'Markdown поддерживается: **жирный**, *курсив*, # заголовок' : ''}
                    sx={{
                        minWidth: '100px',
                        '& .MuiOutlinedInput-input': {
                            p: '0px',
                            cursor: 'text',
                            color: darkMode ? '#ffffff' : '#333',
                            fontSize: '0.8rem',
                            lineHeight: '1.1',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldColors.color,
                            borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                        },
                        '& .MuiInputLabel-root': {
                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                            fontSize: '0.8rem',
                            '&.Mui-focused': {
                                color: fieldColors.color,
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                            borderRadius: '4px',
                        },
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        '& .MuiInputBase-root': {
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                            minWidth: '120px',
                            width: '120px'
                        },
                        '& .MuiInputBase-input': {
                            borderRadius: '4px',
                        },
                    }}
                />
            ))}
            {Array.isArray(localValue) && localValue.length > 1 &&
                <Box></Box>
            }
        </Box>
    );
};

const EditableDateCell = ({ id, field, value, label, saveEditedField, darkMode, equipment }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'plan_start':
            case 'fact_start':
            case 'plan_end':
            case 'fact_end':
                return {
                    color: darkMode ? '#64b5f6' : '#1976d2',
                };
            case 'closing':
                return {
                    color: darkMode ? '#ff8f00' : '#e65100',
                };
            case 'cost':
                return {
                    color: darkMode ? '#e91e63' : '#c2185b',
                };
            case 'comment':
                return {
                    color: darkMode ? '#4caf50' : '#2e7d32',
                };
            default:
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value)

    // Преобразуем строку даты в объект Date для DatePicker
    const getDateFromString = (dateString) => {
        if (!dateString) return null;
        try {
            // Если дата в формате "YYYY-MM-DD" (из базы данных)
            if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return new Date(dateString);
            }

            // Если дата в формате "HH:MM DD-MM-YYYY"
            const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
            if (dateTimeMatch) {
                const [, hours, minutes, day, month, year] = dateTimeMatch;
                return new Date(`${year}-${month}-${day}`);
            }

            // Если дата в формате "DD-MM-YYYY"  
            const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (dateMatch) {
                const [, day, month, year] = dateMatch;
                return new Date(`${year}-${month}-${day}`);
            }

            // Попытка парсинга как обычной даты
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch {
            return null;
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            setLocalValue(formattedDate);
        } else {
            setLocalValue('');
        }
    };

    const handleBlur = () => {
        saveEditedField({
            rowId: id,
            field: field,
            value: localValue,
            isValueChanged: value !== localValue,
            shouldExitEditMode: false,
            equipment: equipment,
        });
    };

    return (
        <Box sx={{ position: 'relative' }}>
            {(() => {
                try {
                    return (
                        <DatePicker
                            selected={getDateFromString(localValue)}
                            // onCalendarClose={handleDateChange}
                            onChange={handleDateChange}
                            onBlur={handleBlur}
                            onCalendarClose={handleBlur}
                            // onClickOutside={handleBlur}

                            locale="ru"
                            dateFormat="dd.MM.yyyy"
                            // placeholderText={label}
                            autoFocus
                            // shouldCloseOnSelect={false}

                            // isClearable
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={10}
                            scrollableYearDropdown
                            autoComplete="off"
                            customInput={
                                <TextField
                                    // fullWidth
                                    // label={label}
                                    size="small"
                                    // variant="standard"
                                    // InputProps={{
                                    //     endAdornment: (
                                    //         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    //             {localValue && (
                                    //                 <IconButton
                                    //                     size="small"
                                    //                     onClick={e => {
                                    //                         e.stopPropagation();
                                    //                         setLocalValue('');
                                    //                     }}
                                    //                     edge="end"
                                    //                     tabIndex={-1}
                                    //                     sx={{
                                    //                         color: darkMode ? '#f44336' : '#d32f2f',
                                    //                         p: 0.1,
                                    //                         '&:hover': {
                                    //                             bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                    //                         },
                                    //                     }}
                                    //                 >
                                    //                     <ClearIcon fontSize="small" />
                                    //                 </IconButton>
                                    //             )}
                                    //             <IconButton
                                    //                 size="small"
                                    //                 onClick={e => {
                                    //                     e.stopPropagation();
                                    //                     handleBlur();
                                    //                 }}
                                    //                 edge="end"
                                    //                 tabIndex={-1}
                                    //                 sx={{
                                    //                     color: darkMode ? '#4caf50' : '#2e7d32',
                                    //                     p: 0.1,
                                    //                     '&:hover': {
                                    //                         bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                                    //                     }
                                    //                 }}
                                    //             >
                                    //                 <SaveIcon fontSize="small" />
                                    //             </IconButton>
                                    //         </Box>
                                    //     ),
                                    // }}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            p: '5px',
                                            cursor: 'pointer',
                                            color: darkMode ? '#ffffff' : '#333',
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: fieldColors.color,
                                            borderWidth: '2px',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                            fontSize: '0.875rem',
                                            '&.Mui-focused': {
                                                color: fieldColors.color,
                                            }
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                            borderRadius: '4px',
                                        },
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        '& .MuiInputBase-root': {
                                            // pr: 0,
                                            fontWeight: 500,
                                            fontSize: '0.8rem',
                                            borderRadius: '4px',
                                            // minWidth: '140px',
                                            width: '90px'
                                        },
                                        '& .MuiInputBase-input': {
                                            borderRadius: '4px',
                                        },
                                    }}
                                />
                            }
                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                        />
                    );
                } catch (error) {
                    console.error('DatePicker error:', error);
                    return null;
                }
            })()}
        </Box>
    );
}

// Функция для открытия обзора опоры в новой вкладке
const openObjectView = (object) => {
    if (!object.coords && !object.x_y_coord) {
        console.warn('Нет координат для опоры:', object);
        return;
    }


    // Определяем тип трансформатора на основе type объекта или используем дефолтный 'transformer'
    const transformerType = object.type || 'transformer';

    // Создаем base64 строку с данными объекта
    const base64 = btoa(JSON.stringify({
        type: transformerType,
        id: object.fid,
    }));

    // Формируем URL с параметром view
    const url = `${window.location.origin}/an?view=${encodeURIComponent(base64)}`;

    // Открываем новую вкладку
    window.open(url, '_blank');
};

function rowContent(_index, row, { editMode, editing, setEditingField, openHistoryDialog, openCommentDialog, openNameByActsDialog, saveEditedField, darkMode, users, equipmentMap, editedRows, selectedRows, handleRowSelection, handleOpenEditDialog, visibleColumns, handleMoveEquipment, searchQuery, advancedFilters, getColumnWidth }) {
    // Если это padding элемент, возвращаем пустую строку
    // if (row.isPadding) {
    //     return (
    //         <React.Fragment>
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //             <TableCell sx={{ height: '1px', padding: 0, border: 'none' }} />
    //         </React.Fragment>
    //     );
    // }
    if (!row.rowSpan) return null
    if (row.isPadding) {
        // Получаем только видимые столбцы с учетом colSpan
        const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);
        const paddingCells = [];

        visibleColumnsArray.forEach(column => {
            if (column.colSpan && column.colSpan > 1) {
                // Для столбцов с colSpan > 1 добавляем соответствующее количество ячеек
                for (let i = 0; i < column.colSpan; i++) {
                    paddingCells.push(
                        <TableCell key={`${column.id}_${i}`} sx={{ height: '1px', padding: 0, border: 'none' }} />
                    );
                }
            } else {
                // Для обычных столбцов добавляем одну ячейку
                paddingCells.push(
                    <TableCell key={column.id} sx={{ height: '1px', padding: 0, border: 'none' }} />
                );
            }
        });

        return (
            <React.Fragment>
                {paddingCells}
            </React.Fragment>
        );
    }

    // Функция для определения видимости подстолбцов на основе видимости основных столбцов
    const isSubColumnVisible = (subColumnName) => {
        // Определяем, к какому основному столбцу относится подстолбец
        if (subColumnName === 'plan_start' || subColumnName === 'fact_start') {
            // Подстолбцы для столбца 'start' с colSpan: 2
            return visibleColumns['start'];
        }
        if (subColumnName === 'plan_end' || subColumnName === 'fact_end') {
            // Подстолбцы для столбца 'end' с colSpan: 2
            return visibleColumns['end'];
        }
        // Для остальных столбцов проверяем их видимость напрямую
        return visibleColumns[subColumnName] || false;
    };

    // console.log(row)
    // const getStageColor = (stage) => {
    //     const stageStr = String(stage || '');
    //     switch (stageStr) {
    //         case 'Проектирование':
    //             return { bg: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)', color: darkMode ? '#ffb74d' : '#f57c00' };
    //         case 'Строительство':
    //             return { bg: darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)', color: darkMode ? '#64b5f6' : '#1976d2' };
    //         case 'Завершено':
    //             return { bg: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)', color: darkMode ? '#81c784' : '#388e3c' };
    //         default:
    //             return { bg: darkMode ? 'rgba(158, 158, 158, 0.2)' : 'rgba(158, 158, 158, 0.1)', color: darkMode ? '#bdbdbd' : '#616161' };
    //     }
    // };

    // const stageColors = getStageColor(row.stage);
    // Проверяем, была ли строка изменена
    const isRowEdited = editedRows[row.id];
    // const planStart = editedRows[row.id]?.changes?.plan_start?.v || row.plan_start?.v;
    // const planEnd = editedRows[row.id]?.changes?.plan_end?.v || row.plan_end?.v;
    // const factStart = editedRows[row.id]?.changes?.fact_start?.v || row.fact_start?.v;
    // const factEnd = editedRows[row.id]?.changes?.fact_end?.v || row.fact_end?.v;
    // const stage = editedRows[row.id]?.changes?.stage?.v || row.stage?.v;
    // const closing = editedRows[row.id]?.changes?.closing?.v || row.closing?.v;
    // const cost = editedRows[row.id]?.changes?.cost?.v || row.cost?.v;
    // const comment = editedRows[row.id]?.changes?.comment?.v || row.comment?.v;
    // const equipment = editedRows[row.id]?.changes?.equipment?.v || row.equipment?.v;
    // const responsible = editedRows[row.id]?.changes?.responsible?.v || row.responsible?.v;
    // const coexecutors = editedRows[row.id]?.changes?.coexecutors?.v || row.coexecutors?.v;

    const planStart = row.plan_start?.v;
    const planEnd = row.plan_end?.v;
    const factStart = row.fact_start?.v;
    const factEnd = row.fact_end?.v;
    const stage = row.stage?.v;
    const closing = row.closing?.v;
    const cost = row.cost?.v;
    const comment = row.comment?.v;
    const nameByActs = row.name_by_acts?.v;
    const equipment = row.equipment?.v;
    const responsible = row.responsible?.v;
    const coexecutors = row.coexecutors?.v;
    const rowSpan = row.rowSpan
    const equipmentIndex = row.equipmentIndex
    const isStartRow = equipmentIndex === 0
    const isEndRow = (equipment.length <= 1 ? 0 : equipment.length) === equipmentIndex

    // Для каждой строки получаем значения из массивов по индексу
    const currentPlanStart = Array.isArray(planStart) ? planStart[equipmentIndex - 1] : planStart;
    const currentPlanEnd = Array.isArray(planEnd) ? planEnd[equipmentIndex - 1] : planEnd;
    const currentFactStart = Array.isArray(factStart) ? factStart[equipmentIndex - 1] : factStart;
    const currentFactEnd = Array.isArray(factEnd) ? factEnd[equipmentIndex - 1] : factEnd;
    const currentStage = Array.isArray(stage) ? stage[equipmentIndex - 1] : stage;
    const currentClosing = equipment.length > 0 ? closing?.[equipmentIndex - 1] : closing?.[0];
    const currentCost = equipment.length > 0 ? cost?.[equipmentIndex - 1] : cost?.[0];
    const currentComment = equipment.length > 0 ? comment?.[equipmentIndex - 1] : comment?.[0];
    const currentNameByActs = equipment.length > 0 ? nameByActs?.[equipmentIndex - 1] : nameByActs?.[0];
    const currentResponsible = Array.isArray(responsible) ? responsible[equipmentIndex - 1] : responsible;
    const currentCoexecutors = Array.isArray(coexecutors[0]) ? coexecutors[equipmentIndex - 1] : coexecutors;
    const currentEquipment = equipment.length > 0 ? equipment?.[equipmentIndex - 1] : equipment?.[0];

    return (
        <React.Fragment key={`${row.id}-${equipmentIndex}`}>
            {/* Номер (fid) - с rowSpan только для первой строки */}
            {equipmentIndex === 0 && visibleColumns['fid'] ? (
                <TableCell rowSpan={rowSpan} align="center" sx={{
                    padding: '8px',
                    color: darkMode ? '#f5f7fa' : '#333',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    position: 'relative',
                    ...(isRowEdited && {
                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                        borderLeft: `3px solid ${darkMode ? '#ffb74d' : '#f57c00'}`,
                    }),
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        editMode && setEditingField({ id: row.id, field: 'fid' })
                    }}
                >
                    {editMode && editing.id === row.id && editing.field === 'fid' ?
                        <EditableNumberCell
                            id={row.id}
                            field={editing.field}
                            label={'Номер'}
                            value={row.fid}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                        />
                        :
                        editMode ? (
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                            >
                                <Checkbox
                                    checked={selectedRows.has(row.id)}
                                    onChange={(e) => {
                                        e.stopPropagation()
                                        handleRowSelection(row.id, e.target.checked)
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                    }}
                                    size="small"
                                    sx={{
                                        p: '2px',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        '&.Mui-checked': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                    }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {row.fid}
                                </Typography>
                            </Box>
                        ) : (
                            row.fid
                        )
                    }
                </TableCell>
            ) : null}

            {/* Наименование (name) - с rowSpan только для первой строки */}
            {equipmentIndex === 0 && visibleColumns['name'] ? (
                <TableCell rowSpan={rowSpan} sx={{
                    padding: '8px',
                    color: darkMode ? '#f5f7fa' : '#333',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    ...(isRowEdited && {
                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                    })
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                openObjectView(row.object);
                            }}
                            sx={{
                                padding: '2px',
                                color: '#2196F3',
                                '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                    color: '#1976D2'
                                }
                            }}
                            title="Открыть обзор опоры в новой вкладке"
                        >
                            <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                                <Box style={{ position: 'absolute', margin: '5px 0 0 6px' }}>
                                    {ktpIcon('default', 20, 20)}
                                </Box>
                                {['2', '3', '4', '7'].includes(row.object?.layer) ? (
                                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                        {projectStatusOreolIcon('ktp', Number(row.object?.layer), 32)}
                                    </Box>
                                ) : null}
                            </Box>
                        </IconButton>
                        <Typography variant='body2' sx={{ flex: 1, fontSize: '0.85rem' }}>
                            {row.object?.name || ''}{row.object?.town ? ` (${row.object.town})` : ''}
                        </Typography>
                        {editMode && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEditDialog(row);
                                }}
                                sx={{
                                    padding: '2px',
                                    color: '#4caf50',
                                    '&:hover': {
                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                        color: '#388e3c'
                                    }
                                }}
                                title="Редактировать объект"
                            >
                                <EditIcon sx={{ fontSize: { xs: '12px', md: '16px' } }} />
                            </IconButton>
                        )}
                        {isRowEdited && (
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: darkMode ? '#ffb74d' : '#f57c00',
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 1 },
                                        '50%': { opacity: 0.5 },
                                        '100%': { opacity: 1 },
                                    }
                                }}
                            />
                        )}
                    </Box>
                </TableCell>
            ) : null}

            {/* name_by_acts - для каждой строки */}
            {visibleColumns['name_by_acts'] && (
                <TableCell align="center" sx={{
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(33, 150, 243, 0.15)' : 'rgba(21, 101, 192, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        editMode && openNameByActsDialog(row)
                    }}
                >
                    {currentNameByActs && (
                        <Box sx={{
                            textAlign: 'left',
                            color: darkMode ? '#2196f3' : '#1565c0',
                            fontWeight: 500,
                            lineHeight: '1.1',
                            maxHeight: '120px',
                            overflow: 'auto',
                            padding: '2px',
                            borderRadius: '2px',
                        }}>
                            <MarkdownContent content={currentNameByActs || ''} darkMode={darkMode} />
                        </Box>
                    )}
                </TableCell>
            )}

            {/* plan_start - для каждой строки или с rowSpan если не массив */}
            {(Array.isArray(planStart) || equipmentIndex === 0) && visibleColumns['start'] ? (
                <TableCell rowSpan={Array.isArray(planStart) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    width: getColumnWidth ? getColumnWidth('start') / 2 : columns[2].width / 2,
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(planStart) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'plan_start', index: Array.isArray(planStart) ? (equipmentIndex - 1) : equipmentIndex })
                    }}
                >
                    {equipmentIndex === 0 && editMode && equipment.length > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            left: '0px',
                            top: '0px'
                        }}>
                            <Checkbox
                                checked={Array.isArray(planStart)}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'plan_start',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'fact_start',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                size="small"
                                sx={{
                                    p: 0,
                                    color: darkMode ? '#60F66D' : '#3DD219',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#60F66D' : '#3DD219',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {editMode && editing.id === row.id && editing.field === 'plan_start' && editing.index === (Array.isArray(planStart) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableDateCell
                            id={row.id}
                            field={editing.field}
                            label={'Плановое начало'}
                            value={Array.isArray(planStart) ? planStart[editing.index] : planStart}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={Array.isArray(planStart) ? currentEquipment : null}
                        />
                        :
                        currentPlanStart && (
                            <Typography
                                sx={{
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'plan_start', index: Array.isArray(planStart) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {formatDate(currentPlanStart, true)}
                            </Typography>
                            // <Chip
                            //     label={formatDate(currentPlanStart, true)}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'plan_start', index: Array.isArray(planStart) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            //         color: darkMode ? '#64b5f6' : '#1976d2',
                            //         fontWeight: 500,
                            //         fontSize: '0.75rem',
                            //         height: 'auto',
                            //         minHeight: '24px',
                            //         maxWidth: '100%',
                            //         textWrap: 'nowrap',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)',
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.1',
                            //             padding: '2px 4px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}

            {/* fact_start - для каждой строки или с rowSpan если не массив */}
            {(Array.isArray(factStart) || equipmentIndex === 0) && visibleColumns['start'] ? (
                <TableCell rowSpan={Array.isArray(factStart) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    width: getColumnWidth ? getColumnWidth('start') / 2 : columns[2].width / 2,
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(factStart) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'fact_start', index: Array.isArray(factStart) ? (equipmentIndex - 1) : equipmentIndex })
                    }}
                >
                    {editMode && editing.id === row.id && editing.field === 'fact_start' && editing.index === (Array.isArray(factStart) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableDateCell
                            id={row.id}
                            field={editing.field}
                            label={'Фактическое начало'}
                            value={Array.isArray(factStart) ? factStart[editing.index] : factStart}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={Array.isArray(factStart) ? currentEquipment : null}
                        />
                        :
                        currentFactStart && (
                            <Typography
                                sx={{
                                    color: currentFactStart
                                        ? (darkMode ? '#4caf50' : '#2e7d32')
                                        : (darkMode ? '#999' : '#666'),
                                    bgcolor: currentFactStart
                                        ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(46, 125, 50, 0.1)')
                                        : (darkMode ? 'rgba(158, 158, 158, 0.2)' : 'rgba(117, 117, 117, 0.1)'),
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'fact_start', index: Array.isArray(factStart) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {formatDate(currentFactStart, true)}
                            </Typography>
                            // <Chip
                            //     label={formatDate(currentFactStart, true)}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'fact_start', index: Array.isArray(factStart) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         bgcolor: currentFactStart
                            //             ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(46, 125, 50, 0.1)')
                            //             : (darkMode ? 'rgba(158, 158, 158, 0.2)' : 'rgba(117, 117, 117, 0.1)'),
                            //         color: currentFactStart
                            //             ? (darkMode ? '#4caf50' : '#2e7d32')
                            //             : (darkMode ? '#999' : '#666'),
                            //         fontWeight: currentFactStart ? 500 : 400,
                            //         fontSize: '0.75rem',
                            //         fontStyle: currentFactStart ? 'normal' : 'italic',
                            //         height: 'auto',
                            //         minHeight: '24px',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: currentFactStart
                            //                 ? (darkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(46, 125, 50, 0.2)')
                            //                 : (darkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(117, 117, 117, 0.2)'),
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.1',
                            //             padding: '2px 4px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}

            {/* plan_end - для каждой строки или с rowSpan если не массив */}
            {(Array.isArray(planEnd) || equipmentIndex === 0) && visibleColumns['end'] ? (
                <TableCell rowSpan={Array.isArray(planEnd) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    width: getColumnWidth ? getColumnWidth('end') / 2 : columns[3].width / 2,
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(planEnd) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'plan_end', index: Array.isArray(planEnd) ? (equipmentIndex - 1) : equipmentIndex })
                    }}

                >
                    {equipmentIndex === 0 && editMode && equipment.length > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            left: '0px',
                            top: '0px'
                        }}>
                            <Checkbox
                                checked={Array.isArray(planEnd)}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'plan_end',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'fact_end',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                size="small"
                                sx={{
                                    p: 0,
                                    color: darkMode ? '#60F66D' : '#3DD219',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#60F66D' : '#3DD219',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {editMode && editing.id === row.id && editing.field === 'plan_end' && editing.index === (Array.isArray(planEnd) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableDateCell
                            id={row.id}
                            field={editing.field}
                            label={'Плановое завершение'}
                            value={Array.isArray(planEnd) ? planEnd[editing.index] : planEnd}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={Array.isArray(planEnd) ? currentEquipment : null}
                        />
                        :
                        currentPlanEnd && (
                            <Typography
                                sx={{
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'plan_end', index: Array.isArray(planEnd) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {formatDate(currentPlanEnd, true)}
                            </Typography>
                            // <Chip
                            //     label={formatDate(currentPlanEnd, true)}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'plan_end', index: Array.isArray(planEnd) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            //         color: darkMode ? '#64b5f6' : '#1976d2',
                            //         fontWeight: 500,
                            //         fontSize: '0.75rem',
                            //         height: 'auto',
                            //         minHeight: '24px',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)',
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.1',
                            //             padding: '2px 4px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}



            {/* fact_end - для каждой строки или с rowSpan если не массив */}
            {(Array.isArray(factEnd) || equipmentIndex === 0) && visibleColumns['end'] ? (
                <TableCell rowSpan={Array.isArray(factEnd) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    width: getColumnWidth ? getColumnWidth('end') / 2 : columns[3].width / 2,
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(factEnd) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'fact_end', index: Array.isArray(factEnd) ? (equipmentIndex - 1) : equipmentIndex })
                    }}
                >
                    {editMode && editing.id === row.id && editing.field === 'fact_end' && editing.index === (Array.isArray(factEnd) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableDateCell
                            id={row.id}
                            field={editing.field}
                            label={'Фактическое завершение'}
                            value={Array.isArray(factEnd) ? factEnd[editing.index] : factEnd}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={Array.isArray(factEnd) ? currentEquipment : null}
                        />
                        :
                        currentFactEnd && (
                            <Typography
                                sx={{
                                    color: currentFactEnd
                                        ? (darkMode ? '#4caf50' : '#2e7d32')
                                        : (darkMode ? '#999' : '#666'),
                                    bgcolor: currentFactEnd
                                        ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(46, 125, 50, 0.1)')
                                        : (darkMode ? 'rgba(158, 158, 158, 0.2)' : 'rgba(117, 117, 117, 0.1)'),
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'fact_end', index: Array.isArray(factEnd) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {formatDate(currentFactEnd, true)}
                            </Typography>
                            // <Chip
                            //     label={formatDate(currentFactEnd, true)}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'fact_end', index: Array.isArray(factEnd) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         bgcolor: currentFactEnd
                            //             ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(46, 125, 50, 0.1)')
                            //             : (darkMode ? 'rgba(158, 158, 158, 0.2)' : 'rgba(117, 117, 117, 0.1)'),
                            //         color: currentFactEnd
                            //             ? (darkMode ? '#4caf50' : '#2e7d32')
                            //             : (darkMode ? '#999' : '#666'),
                            //         fontWeight: currentFactEnd ? 500 : 400,
                            //         fontSize: '0.75rem',
                            //         fontStyle: currentFactEnd ? 'normal' : 'italic',
                            //         height: 'auto',
                            //         minHeight: '24px',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: currentFactEnd
                            //                 ? (darkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(46, 125, 50, 0.2)')
                            //                 : (darkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(117, 117, 117, 0.2)'),
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.1',
                            //             padding: '2px 4px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}



            {/* stage - для каждой строки */}
            {(Array.isArray(stage) || equipmentIndex === 0) && visibleColumns['stage'] ? (
                <TableCell rowSpan={Array.isArray(stage) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(171, 71, 188, 0.15)' : 'rgba(106, 27, 154, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(stage) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'stage', index: Array.isArray(stage) ? (equipmentIndex - 1) : equipmentIndex })
                    }}
                >
                    {equipmentIndex === 0 && editMode && equipment.length > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            left: '0px',
                            top: '0px'
                        }}>
                            <Checkbox
                                checked={Array.isArray(stage)}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'stage',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                size="small"
                                sx={{
                                    p: 0,
                                    color: darkMode ? '#60F66D' : '#3DD219',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#60F66D' : '#3DD219',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {editMode && editing.id === row.id && editing.field === 'stage' && editing.index === (Array.isArray(stage) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableNumberCell
                            id={row.id}
                            field={editing.field}
                            label={'Номер стадии'}
                            value={Array.isArray(stage) ? stage[editing.index] : stage}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={Array.isArray(stage) ? currentEquipment : null}
                        />
                        :
                        currentStage && (
                            <Typography
                                sx={{
                                    ...getStageColor(currentStage, darkMode),
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'stage', index: Array.isArray(stage) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {String(currentStage || '')}
                            </Typography>
                            // <Chip
                            //     label={String(currentStage || '')}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'stage', index: Array.isArray(stage) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         ...getStageColor(currentStage, darkMode),
                            //         fontWeight: 500,
                            //         fontSize: '0.75rem',
                            //         height: 'auto',
                            //         minHeight: '20px',
                            //         width: '100%',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: getStageColor(currentStage, darkMode)?.hoverBgcolor,
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.2',
                            //             padding: '2px 8px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}

            {/* closing - для каждой строки */}
            {visibleColumns['closing'] && (
                <TableCell align="center" sx={{
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255, 143, 0, 0.15)' : 'rgba(230, 81, 0, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (equipment.length > 0 && equipmentIndex === 0) return
                        console.log(equipmentIndex)
                        editMode && setEditingField({ id: row.id, field: 'closing', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                    }}
                >
                    {editMode && editing.id === row.id && editing.field === 'closing' && editing.index === (equipment.length === 0 ? 0 : equipmentIndex - 1) ?
                        <EditableNumberCell
                            id={row.id}
                            field={editing.field}
                            label={'Номер закрытия'}
                            value={closing[editing.index || 0]}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={currentEquipment}

                        />
                        :
                        (
                            equipment.length > 0 && equipmentIndex === 0 ?
                                <Typography
                                    sx={{
                                        color: darkMode ? '#ff8f00' : '#e65100',
                                        borderColor: darkMode ? '#ff8f00' : '#e65100',
                                        border: '1px solid',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        '&:hover': editMode ? {
                                            transform: 'scale(1.02)',
                                        } : {},
                                    }}
                                    onClick={() => { }}
                                >
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(closing.slice(0, equipment.length).reduce((acc, item) => acc + item, 0) || 0)}
                                </Typography>
                                // <Chip
                                //     label={new Intl.NumberFormat('ru-RU', {
                                //         style: 'currency',
                                //         currency: 'RUB',
                                //     }).format(closing.slice(0, equipment.length).reduce((acc, item) => acc + item, 0) || 0)}
                                //     size="small"
                                //     variant='outlined'
                                //     onClick={() => { }}
                                //     // disabled={true}
                                //     sx={{
                                //         // bgcolor: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                                //         borderColor: darkMode ? '#ff8f00' : '#e65100',
                                //         color: darkMode ? '#ff8f00' : '#e65100',
                                //         fontWeight: 500,
                                //         width: '100%',
                                //         fontSize: '0.75rem',
                                //     }}
                                // />
                                :
                                <Typography
                                    sx={{
                                        color: darkMode ? '#ff8f00' : '#e65100',
                                        bgcolor: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        '&:hover': editMode ? {
                                            transform: 'scale(1.02)',
                                        } : {},
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        console.log(equipmentIndex)
                                        editMode && setEditingField({ id: row.id, field: 'closing', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                                    }}
                                >
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(currentClosing || 0)}
                                </Typography>
                            // <Chip
                            //     label={new Intl.NumberFormat('ru-RU', {
                            //         style: 'currency',
                            //         currency: 'RUB',
                            //     }).format(currentClosing || 0)}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         console.log(equipmentIndex)
                            //         editMode && setEditingField({ id: row.id, field: 'closing', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                            //     }}
                            //     sx={{
                            //         bgcolor: darkMode ? 'rgba(255, 143, 0, 0.2)' : 'rgba(230, 81, 0, 0.1)',
                            //         color: darkMode ? '#ff8f00' : '#e65100',
                            //         fontWeight: 500,
                            //         fontSize: '0.75rem',
                            //         height: 'auto',
                            //         minHeight: '20px',
                            //         width: '100%',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: darkMode ? 'rgba(255, 143, 0, 0.3)' : 'rgba(230, 81, 0, 0.2)',
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.1',
                            //             padding: '2px 4px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            )}

            {/* cost - для каждой строки */}
            {visibleColumns['cost'] && (
                <TableCell align="center" sx={{
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(233, 30, 99, 0.15)' : 'rgba(194, 24, 91, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (equipment.length > 0 && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'cost', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                    }}
                >
                    {editMode && editing.id === row.id && editing.field === 'cost' && editing.index === (equipment.length === 0 ? 0 : equipmentIndex - 1) ?
                        <EditableNumberCell
                            id={row.id}
                            field={editing.field}
                            label={'Стоимость'}
                            value={cost[editing.index || 0]}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            equipment={currentEquipment}
                        />
                        :
                        (
                            equipment.length > 0 && equipmentIndex === 0 ?
                                <Typography
                                    sx={{
                                        color: darkMode ? '#e91e63' : '#c2185b',
                                        borderColor: darkMode ? '#e91e63' : '#c2185b',
                                        border: '1px solid',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        '&:hover': editMode ? {
                                            transform: 'scale(1.02)',
                                        } : {},
                                    }}
                                    onClick={() => { }}
                                >
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(cost.slice(0, equipment.length).reduce((acc, item) => acc + item, 0) || 0)}
                                </Typography>
                                // <Chip
                                //     label={new Intl.NumberFormat('ru-RU', {
                                //         style: 'currency',
                                //         currency: 'RUB',
                                //     }).format(cost.slice(0, equipment.length).reduce((acc, item) => acc + item, 0) || 0)}
                                //     size="small"
                                //     variant='outlined'
                                //     onClick={() => { }}
                                //     // disabled={true}
                                //     sx={{
                                //         // bgcolor: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                                //         borderColor: darkMode ? '#e91e63' : '#c2185b',
                                //         color: darkMode ? '#e91e63' : '#c2185b',
                                //         fontWeight: 500,
                                //         width: '100%',
                                //         fontSize: '0.75rem',
                                //     }}
                                // />
                                :
                                <Typography
                                    sx={{
                                        color: darkMode ? '#e91e63' : '#c2185b',
                                        bgcolor: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        '&:hover': editMode ? {
                                            transform: 'scale(1.02)',
                                        } : {},
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        editMode && setEditingField({ id: row.id, field: 'cost', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                                    }}
                                >
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(currentCost || 0)}
                                </Typography>
                            // <Chip
                            //     label={new Intl.NumberFormat('ru-RU', {
                            //         style: 'currency',
                            //         currency: 'RUB',
                            //     }).format(currentCost || 0)}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'cost', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                            //     }}
                            //     sx={{
                            //         bgcolor: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                            //         color: darkMode ? '#e91e63' : '#c2185b',
                            //         fontWeight: 500,
                            //         fontSize: '0.75rem',
                            //         height: 'auto',
                            //         minHeight: '20px',
                            //         width: '100%',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: darkMode ? 'rgba(233, 30, 99, 0.3)' : 'rgba(194, 24, 91, 0.2)',
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.1',
                            //             padding: '2px 4px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            )}

            {/* comment - для каждой строки */}
            {visibleColumns['comment'] && (
                <TableCell align="center" sx={{
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(33, 150, 243, 0.15)' : 'rgba(21, 101, 192, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        editMode && openCommentDialog(row)
                    }}
                >
                    {currentComment && (
                        <Box sx={{
                            textAlign: 'left',
                            color: darkMode ? '#2196f3' : '#1565c0',
                            fontWeight: 500,
                            lineHeight: '1.1',
                            maxHeight: '120px',
                            overflow: 'auto',
                            padding: '2px',
                            borderRadius: '2px',
                        }}>
                            <MarkdownContent content={currentComment || ''} darkMode={darkMode} />
                        </Box>
                    )}
                </TableCell>
            )}

            {/* responsible - для каждой строки или с rowSpan если не массив */}
            {(Array.isArray(responsible) || equipmentIndex === 0) && visibleColumns['responsible'] ? (
                <TableCell rowSpan={Array.isArray(responsible) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(responsible) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'responsible', index: Array.isArray(responsible) ? (equipmentIndex - 1) : equipmentIndex })
                    }}
                >
                    {equipmentIndex === 0 && editMode && equipment.length > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            left: '0px',
                            top: '0px'
                        }}>
                            <Checkbox
                                checked={Array.isArray(responsible)}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'responsible',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                size="small"
                                sx={{
                                    p: 0,
                                    color: darkMode ? '#60F66D' : '#3DD219',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#60F66D' : '#3DD219',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {editMode && editing.id === row.id && editing.field === 'responsible' && editing.index === (Array.isArray(responsible) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableSelectCell
                            id={row.id}
                            field={editing.field}
                            label={'Выберите ответственного'}
                            value={users[currentResponsible]}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            options={Object.entries(users).map(([id, name]) => ({ id: Number(id), name })).filter(o => currentResponsible != o.id)}
                            multiple={false}
                            equipment={Array.isArray(responsible) ? currentEquipment : null}
                        />
                        :
                        currentResponsible && (
                            <Typography
                                sx={{
                                    bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                                    color: darkMode ? '#f44336' : '#d32f2f',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'responsible', index: Array.isArray(responsible) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {users[currentResponsible]}
                            </Typography>
                            // <Chip
                            //     label={users[currentResponsible]}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'responsible', index: Array.isArray(responsible) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                            //         color: darkMode ? '#f44336' : '#d32f2f',
                            //         fontSize: '0.7rem',
                            //         height: 'auto',
                            //         minHeight: '24px',
                            //         width: '100%',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: darkMode ? 'rgba(211, 47, 47, 0.3)' : 'rgba(211, 47, 47, 0.2)',
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.2',
                            //             padding: '4px 8px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}

            {/* coexecutors - для каждой строки или с rowSpan если не массив */}
            {((Array.isArray(coexecutors) && Array.isArray(coexecutors[0])) || equipmentIndex === 0) && visibleColumns['coexecutors'] ? (
                <TableCell rowSpan={Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) ? 1 : rowSpan} align="center" sx={{
                    position: 'relative',
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(245, 124, 0, 0.15)' : 'rgba(245, 124, 0, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) && equipmentIndex === 0) return
                        editMode && setEditingField({ id: row.id, field: 'coexecutors', index: Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) ? (equipmentIndex - 1) : equipmentIndex })
                    }}
                >
                    {equipmentIndex === 0 && editMode && equipment.length > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            left: '0px',
                            top: '0px'
                        }}>
                            <Checkbox
                                checked={Array.isArray(coexecutors[0]) && coexecutors.length === equipment.length}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    saveEditedField({
                                        rowId: row.id,
                                        field: 'coexecutors',
                                        action: 'multiple',
                                        value: null,
                                        isValueChanged: true
                                    })
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                size="small"
                                sx={{
                                    p: 0,
                                    color: darkMode ? '#60F66D' : '#3DD219',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#60F66D' : '#3DD219',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {editMode && editing.id === row.id && editing.field === 'coexecutors' && editing.index === (Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) ? (equipmentIndex - 1) : equipmentIndex) ?
                        <EditableSelectCell
                            id={row.id}
                            field={editing.field}
                            label={'Выберите соисполнителей'}
                            value={currentCoexecutors.map(c => ({ id: c, name: users[c] }))}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            options={Object.entries(users).map(([id, name]) => ({ id: Number(id), name })).filter(o => !currentCoexecutors.includes(o.id))}
                            multiple={true}
                            equipment={Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) ? currentEquipment : null}
                        />
                        :
                        currentCoexecutors && Array.isArray(currentCoexecutors) && currentCoexecutors.length > 0 && (
                            <Typography
                                sx={{
                                    bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                    color: darkMode ? '#ffb74d' : '#f57c00',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    '&:hover': editMode ? {
                                        transform: 'scale(1.02)',
                                    } : {},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    editMode && setEditingField({ id: row.id, field: 'coexecutors', index: Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) ? (equipmentIndex - 1) : equipmentIndex })
                                }}
                            >
                                {Array.isArray(currentCoexecutors) ? currentCoexecutors.map(i => users[i]).join(', ') : users[currentCoexecutors] || ''}
                            </Typography>
                            // <Chip
                            //     label={Array.isArray(currentCoexecutors) ? currentCoexecutors.map(i => users[i]).join(', ') : users[currentCoexecutors] || ''}
                            //     size="small"
                            //     onClick={(e) => {
                            //         e.stopPropagation()
                            //         editMode && setEditingField({ id: row.id, field: 'coexecutors', index: Array.isArray(coexecutors) && Array.isArray(coexecutors[0]) ? (equipmentIndex - 1) : equipmentIndex })
                            //     }}
                            //     sx={{
                            //         bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                            //         color: darkMode ? '#ffb74d' : '#f57c00',
                            //         fontSize: '0.7rem',
                            //         height: 'auto',
                            //         minHeight: '24px',
                            //         width: '100%',
                            //         maxWidth: '100%',
                            //         cursor: editMode ? 'pointer' : 'auto',
                            //         '&:hover': editMode ? {
                            //             bgcolor: darkMode ? 'rgba(245, 124, 0, 0.3)' : 'rgba(245, 124, 0, 0.2)',
                            //             transform: 'scale(1.02)',
                            //         } : {},
                            //         transition: 'all 0.2s ease',
                            //         '& .MuiChip-label': {
                            //             whiteSpace: 'normal',
                            //             wordBreak: 'break-word',
                            //             textAlign: 'center',
                            //             lineHeight: '1.2',
                            //             padding: '4px 8px',
                            //         },
                            //     }}
                            // />
                        )
                    }
                </TableCell>
            ) : null}

            {/* equipment - для каждой строки */}
            {/* {equipmentIndex === 0 && ( */}
            {((editMode && editing.id === row.id && editing.field === 'equipment' && equipmentIndex === 0) || !editMode || (editMode && (editing.id !== row.id || editing.field !== 'equipment'))) && visibleColumns['equipment'] ? (
                <TableCell rowSpan={editMode && editing.id === row.id && editing.field === 'equipment' ? rowSpan : 1} align="center" sx={{
                    padding: isStartRow ? '4px 8px 2px' : isEndRow ? '2px 8px 4px' : '2px 8px',
                    height: '1px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(92, 107, 192, 0.15)' : 'rgba(57, 73, 171, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                    onClick={(e) => {
                        e.stopPropagation()
                        editMode && equipmentIndex === 0 && setEditingField({ id: row.id, field: 'equipment' })
                    }}
                >
                    {editMode && editing.id === row.id && editing.field === 'equipment' ?
                        <EditableSelectCell
                            id={row.id}
                            field={editing.field}
                            label={'Добавить оснащение...'}
                            value={[]}
                            saveEditedField={saveEditedField}
                            darkMode={darkMode}
                            options={Object.entries(equipmentMap).map(([id, name]) => ({ id, name })).filter(o => !equipment.includes(o.id))}
                            multiple={true}
                        />
                        :
                        (
                            equipmentIndex === 0 ?
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {equipment.length > 0 && (
                                        <Typography
                                            sx={{
                                                color: darkMode ? '#7486F0' : '#4154C4',
                                                borderColor: darkMode ? '#7486F0' : '#4154C4',
                                                border: '1px solid',
                                                px: 1,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '0.8rem',
                                                '&:hover': editMode ? {
                                                    transform: 'scale(1.02)',
                                                } : {},
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                console.log(equipmentIndex)
                                                editMode && setEditingField({ id: row.id, field: 'closing', index: equipment.length === 0 ? 0 : equipmentIndex - 1 })
                                            }}
                                        >
                                            {`Всего - ${equipment.length}`}
                                        </Typography>
                                        // <Chip
                                        //     label={`Всего - ${equipment.length}`}
                                        //     size="small"
                                        //     variant='outlined'
                                        //     onClick={() => { }}
                                        //     // disabled={true}
                                        //     sx={{
                                        //         // bgcolor: darkMode ? 'rgba(233, 30, 99, 0.2)' : 'rgba(194, 24, 91, 0.1)',
                                        //         borderColor: darkMode ? '#7486F0' : '#4154C4',
                                        //         color: darkMode ? '#7486F0' : '#4154C4',
                                        //         fontWeight: 500,
                                        //         width: '100%',
                                        //         fontSize: '0.75rem',
                                        //     }}
                                        // />
                                    )}
                                    {editMode &&
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                editMode && equipmentIndex === 0 && setEditingField({ id: row.id, field: 'equipment' })
                                            }}
                                            sx={{
                                                // bgcolor: darkMode ? 'rgba(130, 244, 54, 0.2)' : 'rgba(130, 244, 54, 0.1)',
                                                color: darkMode ? '#4caf50' : '#2e7d32',
                                                padding: '2px',
                                                minWidth: '20px',
                                                width: '20px',
                                                height: '20px',
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(130, 244, 54, 0.3)' : 'rgba(130, 244, 54, 0.2)',
                                                },
                                            }}
                                        >
                                            <AddIcon sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    }

                                </Box>
                                :
                                currentEquipment && (
                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: editMode ? 'space-between' : 'center', gap: 0.5 }}>
                                        {/* Кнопки для изменения порядка equipment */}
                                        {editMode && equipment.length > 1 && equipmentIndex > 0 &&
                                            (!advancedFilters || advancedFilters.length === 0) && (
                                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.25 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const actualIndex = equipmentIndex - 1;
                                                            handleMoveEquipment(row.id, actualIndex, 'up');
                                                        }}
                                                        disabled={equipmentIndex === 1}
                                                        sx={{
                                                            padding: '2px',
                                                            minWidth: '20px',
                                                            width: '20px',
                                                            height: '20px',
                                                            color: equipmentIndex === 1
                                                                ? (darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)')
                                                                : (darkMode ? '#64b5f6' : '#1976d2'),
                                                            '&:hover': equipmentIndex !== 1 ? {
                                                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                                            } : {},
                                                            '&.Mui-disabled': {
                                                                color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                                                            },
                                                        }}
                                                    >
                                                        <ArrowUpwardIcon sx={{ fontSize: '14px' }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const actualIndex = equipmentIndex - 1;
                                                            handleMoveEquipment(row.id, actualIndex, 'down');
                                                        }}
                                                        disabled={equipmentIndex === equipment.length}
                                                        sx={{
                                                            padding: '2px',
                                                            minWidth: '20px',
                                                            width: '20px',
                                                            height: '20px',
                                                            color: equipmentIndex === equipment.length
                                                                ? (darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)')
                                                                : (darkMode ? '#64b5f6' : '#1976d2'),
                                                            '&:hover': equipmentIndex !== equipment.length ? {
                                                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                                            } : {},
                                                            '&.Mui-disabled': {
                                                                color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                                                            },
                                                        }}
                                                    >
                                                        <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        <Typography
                                            sx={{
                                                color: darkMode ? '#7486F0' : '#4154C4',
                                                bgcolor: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                                                borderRadius: '8px',
                                                px: 1,
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                '&:hover': editMode ? {
                                                    transform: 'scale(1.02)',
                                                } : {},
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                // editMode && equipmentIndex === 0 && setEditingField({ id: row.id, field: 'equipment' })
                                            }}
                                        >
                                            {equipmentMap[currentEquipment]}
                                        </Typography>
                                        {/* <Chip
                                            label={equipmentMap[currentEquipment]}
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                // editMode && equipmentIndex === 0 && setEditingField({ id: row.id, field: 'equipment' })
                                            }}
                                            sx={{
                                                bgcolor: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                                                color: darkMode ? '#7486F0' : '#4154C4',
                                                fontSize: '0.7rem',
                                                height: 'auto',
                                                minHeight: '24px',
                                                width: '100%',
                                                maxWidth: '100%',
                                                cursor: editMode && equipmentIndex === 0 ? 'pointer' : 'auto',
                                                '&:hover': editMode && equipmentIndex === 0 ? {
                                                    bgcolor: darkMode ? 'rgba(92, 107, 192, 0.3)' : 'rgba(57, 73, 171, 0.2)',
                                                    transform: 'scale(1.02)',
                                                } : {},
                                                transition: 'all 0.2s ease',
                                                '& .MuiChip-label': {
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    textAlign: 'center',
                                                    lineHeight: '1.1',
                                                    padding: '2px 4px',
                                                },
                                            }}
                                        /> */}
                                        {/* IconButton для удаления equipment */}
                                        {editMode && equipment.length > 0 && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    saveEditedField({
                                                        rowId: row.id,
                                                        field: 'equipment',
                                                        value: equipment.filter(e => e !== currentEquipment),
                                                        isValueChanged: true,
                                                        shouldExitEditMode: false,
                                                        deleted: currentEquipment
                                                    })
                                                }}
                                                sx={{
                                                    // bgcolor: darkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
                                                    color: darkMode ? '#f44336' : '#d32f2f',
                                                    padding: '2px',
                                                    minWidth: '20px',
                                                    width: '20px',
                                                    height: '20px',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)',
                                                        // transform: 'scale(1.1)',
                                                    },
                                                    // transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <ClearIcon sx={{ fontSize: '14px' }} />
                                            </IconButton>
                                        )}

                                    </Box>
                                )
                        )
                    }
                </TableCell>
            ) : null}
            {/* )} */}

            {/* Создано - с rowSpan только для первой строки */}
            {equipmentIndex === 0 && visibleColumns['created'] ? (
                <TableCell rowSpan={rowSpan} align="center" sx={{
                    padding: '8px',
                    color: darkMode ? '#bbb' : '#666',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    fontSize: '0.8rem',
                }}>
                    <Box>
                        <Typography variant="body2" sx={{
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            mb: 0.5,
                        }}>
                            {String(users[row.creator] || '')}
                        </Typography>
                        <Typography variant="caption" sx={{
                            color: darkMode ? '#bbb' : '#666',
                            fontSize: '0.75rem',
                        }}>
                            {formatDate(row.created_at)}
                        </Typography>
                    </Box>
                </TableCell>
            ) : null}

            {/* Изменено - с rowSpan только для первой строки */}
            {equipmentIndex === 0 && visibleColumns['modified'] ? (
                <TableCell rowSpan={rowSpan} align="center" sx={{
                    padding: '8px',
                    color: darkMode ? '#bbb' : '#666',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    fontSize: '0.8rem',
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                            <Tooltip title="История изменений">
                                <IconButton
                                    onClick={() => openHistoryDialog(row)}
                                    size="small"
                                    sx={{
                                        color: darkMode ? '#ff9800' : '#f57c00',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)',
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <HistoryIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#f5f7fa' : '#333',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                mb: 0.5,
                            }}>
                                {String(users[row.modifier] || '')}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.75rem',
                            }}>
                                {formatDate(row.modified_at)}
                            </Typography>
                        </Box>
                    </Box>
                </TableCell>
            ) : null}
        </React.Fragment>
    );

}

// Утилиты для работы с localStorage
const STORAGE_KEYS = {
    OBJECTS_REESTR_SETTINGS: 'objects_reestr_settings'
};

const getStoredSettings = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.OBJECTS_REESTR_SETTINGS);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const storeSettings = (settings) => {
    try {
        const current = getStoredSettings();
        const updated = { ...current, ...settings };
        localStorage.setItem(STORAGE_KEYS.OBJECTS_REESTR_SETTINGS, JSON.stringify(updated));
    } catch (error) {
        console.warn('Failed to save objects reestr settings:', error);
    }
};

const ObjectsReestrView = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector(state => state.state.user);
    const darkMode = useSelector(state => state.state.darkMode);
    const dispatch = useDispatch();

    // Получаем сохраненные настройки
    const storedSettings = getStoredSettings();

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [groupedData, setGroupedData] = useState({ groupCounts: [], groupCountsRows: [], groupedData: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { transformers } = useSelector(selectMappedElectricity)
    const [equipment, setEquipment] = useState([]);
    const [projects, setProjects] = useState([]);
    const [responsible, setResponsible] = useState([]);

    const [stages, setStages] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
    const [objectDialogOpen, setObjectDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [nameByActsDialogOpen, setNameByActsDialogOpen] = useState(false);
    const [selectedObject, setSelectedObject] = useState(null);
    const [commentEditObject, setCommentEditObject] = useState(null);
    const [nameByActsEditObject, setNameByActsEditObject] = useState(null);
    const [editMode, setEditMode] = useState(false)
    const [editing, setEditing] = useState({ id: null, field: null })
    const [editedRows, setEditedRows] = useState({})

    // Состояния для поиска и фильтрации
    const [searchQuery, setSearchQuery] = useState('');
    // 'all', 'my', 'stage'
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedProjects, setSelectedProjects] = useState(storedSettings.selectedProjects ?? []);

    // Состояния для продвинутой фильтрации
    const [advancedFilters, setAdvancedFilters] = useState(storedSettings.advancedFilters ?? []);
    const availableFilters = useMemo(() => ([
        { id: 'plan_start', label: 'Начало строительства (плановое)', type: 'date' },
        { id: 'fact_start', label: 'Начало строительства (фактическое)', type: 'date' },
        { id: 'plan_end', label: 'Завершение строительства (плановое)', type: 'date' },
        { id: 'fact_end', label: 'Завершение строительства (фактическое)', type: 'date' },
        { id: 'stage', label: 'Этап', type: 'number' },
        { id: 'closing', label: 'Закрытие без НДС', type: 'number' },
        { id: 'cost', label: 'Стоимость без НДС', type: 'number' },
        { id: 'comment', label: 'Комментарий', type: 'text' },
        { id: 'name_by_acts', label: 'Наименование по актам', type: 'text' },
        { id: 'responsible', label: 'Ответственный', type: 'multi-select', options: Object.entries(responsible).map(([id, name]) => ({ id, name, label: name })) },
        { id: 'coexecutors', label: 'Соисполнители', type: 'multi-select', options: Object.entries(responsible).map(([id, name]) => ({ id, name, label: name })) },
        { id: 'equipment', label: 'Оснащение', type: 'multi-select', options: Object.entries(equipment).map(([id, name]) => ({ id, name, label: name })) },
        { id: 'created_at', label: 'Создано', type: 'date' },
        { id: 'creator', label: 'Создатель', type: 'multi-select', options: Object.entries(responsible).map(([id, name]) => ({ id, name, label: name })) },
        { id: 'modified_at', label: 'Изменено', type: 'date' },
        { id: 'modifier', label: 'Редактор', type: 'multi-select', options: Object.entries(responsible).map(([id, name]) => ({ id, name, label: name })) }
    ]), [responsible, equipment]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Состояние для управления видимостью второй строки с кнопками управления
    const [showControlMenu, setShowControlMenu] = useState(storedSettings.showControlMenu !== false);

    // Состояние для меню экспорта
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

    // Состояние для выбранных строк
    const [selectedRows, setSelectedRows] = useState(new Set());

    // Функция для фильтрации и сортировки данных
    const filterAndSortData = useCallback((rawData, editedRows, search, projectsFilter, advancedFilters, sortBy, sortDirection) => {
        let filtered = [...rawData];

        filtered = filtered.map(f => ({
            ...f,
            ...editedRows[f.id]?.changes
        }))

        // Поиск
        if (search.trim()) {
            // Разбиваем поисковый запрос на отдельные слова и фильтруем пустые
            const searchTerms = search.toLowerCase().split(/\s+/).filter(term => term.length > 0);

            filtered = filtered.filter(item => {
                // Получаем объект трансформера
                const transformer = transformers.get(item.object_id);

                // Подготавливаем все текстовые поля для поиска с учетом структуры .v
                const getValue = (field) => {
                    if (!field) return '';
                    const value = field.v !== undefined ? field.v : field;
                    if (Array.isArray(value)) {
                        return value.map(v => String(v || '')).join(' ');
                    }
                    return String(value || '');
                };

                const objectName = String(transformer?.name || '').toLowerCase();
                const objectTown = String(transformer?.town || '').toLowerCase();
                const stage = getValue(item.stage).toLowerCase();
                const closing = getValue(item.closing).toLowerCase();
                const cost = getValue(item.cost).toLowerCase();
                const comment = getValue(item.comment).toLowerCase();
                const nameByActs = getValue(item.name_by_acts).toLowerCase();

                // Ответственные
                const responsibleValue = getValue(item.responsible);
                const responsibleNames = responsibleValue.split(',').map(id => responsible[id] || '').join(' ').toLowerCase();

                // Соисполнители
                const coexecutorsValue = getValue(item.coexecutors);
                let coexecutorsNames = '';
                if (coexecutorsValue) {
                    try {
                        const coexecutorsArray = JSON.parse(coexecutorsValue);
                        if (Array.isArray(coexecutorsArray)) {
                            if (Array.isArray(coexecutorsArray[0])) {
                                // Вложенный массив
                                coexecutorsNames = coexecutorsArray.map(group =>
                                    group.map(id => responsible[id] || '').join(' ')
                                ).join(' ').toLowerCase();
                            } else {
                                // Простой массив
                                coexecutorsNames = coexecutorsArray.map(id => responsible[id] || '').join(' ').toLowerCase();
                            }
                        }
                    } catch {
                        coexecutorsNames = coexecutorsValue.toLowerCase();
                    }
                }

                // Оснащение
                const equipmentValue = getValue(item.equipment);
                const equipmentNames = equipmentValue.split(',').map(id => equipment[id] || '').join(' ').toLowerCase();

                const creatorName = String(item.creator?.name || '').toLowerCase();
                const modifierName = String(item.modifier?.name || '').toLowerCase();
                const itemId = String(item.id || '').toLowerCase();
                const itemFid = String(item.fid || '').toLowerCase();

                // Объединяем все поля в одну строку для поиска
                const searchableText = [
                    objectName,
                    objectTown,
                    stage,
                    closing,
                    cost,
                    comment,
                    nameByActs,
                    responsibleNames,
                    equipmentNames,
                    coexecutorsNames,
                    creatorName,
                    modifierName,
                    itemId,
                    itemFid
                ].join(' ');

                // Проверяем, что каждое слово из поискового запроса содержится в тексте
                return searchTerms.every(term => searchableText.includes(term));
            });
        }

        // Фильтрация по проектам
        if (projectsFilter && projectsFilter.length > 0) {
            filtered = filtered.filter(item => {
                return projectsFilter.includes(item.project_id);
            });
        }

        // Продвинутая фильтрация
        if (advancedFilters && advancedFilters.length > 0) {
            // Вспомогательные функции для работы с данными
            const getValue = (field) => {
                if (!field) return null;
                const value = field.v !== undefined ? field.v : field;
                return value;
            };

            const getDateValue = (dateField) => {
                if (!dateField) return null;
                const value = getValue(dateField);
                if (!value) return null;
                if (Array.isArray(value)) {
                    const validDates = value
                        .map(d => {
                            try {
                                return new Date(d).getTime();
                            } catch {
                                return null;
                            }
                        })
                        .filter(time => time !== null);
                    return validDates.length > 0 ? Math.max(...validDates) : null;
                }
                try {
                    const time = new Date(value).getTime();
                    return isNaN(time) ? null : time;
                } catch {
                    return null;
                }
            };

            const getNumericValue = (field) => {
                if (!field) return null;
                const value = getValue(field);
                if (!value) return null;
                if (Array.isArray(value)) {
                    const validNumbers = value
                        .map(v => parseFloat(v))
                        .filter(num => !isNaN(num));
                    return validNumbers.length > 0 ? validNumbers.reduce((acc, num) => acc + num, 0) : null;
                }
                const num = parseFloat(value);
                return isNaN(num) ? null : num;
            };

            const getTextValue = (field) => {
                if (!field) return '';
                const value = getValue(field);
                if (!value) return '';
                if (Array.isArray(value)) {
                    return value.map(v => String(v || '')).join(' ');
                }
                return String(value || '');
            };

            const getArrayValue = (field) => {
                if (!field) return [];
                const value = getValue(field);
                if (!value) return [];
                if (Array.isArray(value)) {
                    return value;
                }
                return [value];
            };

            advancedFilters.forEach(filter => {
                const filterType = filter.originalId || filter.id;
                console.log(filterType, filter)
                switch (filterType) {
                    case 'plan_start':
                    case 'fact_start':
                    case 'plan_end':
                    case 'fact_end':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {

                                const itemTime = getDateValue(item[filterType]);
                                console.log(itemTime)
                                if (itemTime === null) return null;
                                if (Array.isArray(itemTime) && itemTime.length === 0) return null;
                                // Определяем какие элементы plan_start соответствуют фильтру
                                let matchingIndices = (Array.isArray(itemTime) ? itemTime : [itemTime]).map((date, index) => {
                                    if (!date) return -1;
                                    const itemDate = new Date(date);
                                    let matches = false;

                                    switch (filter.operator) {
                                        case 'equals':
                                            matches = itemDate.getDate() === filterDate.getDate();
                                            break;
                                        case 'greater':
                                            matches = itemDate > filterDate;
                                            break;
                                        case 'less':
                                            matches = itemDate < filterDate;
                                            break;
                                        case 'between':
                                            if (!filter.value2) matches = true;
                                            else {
                                                const filterDate2 = new Date(filter.value2);
                                                matches = itemDate >= filterDate && itemDate <= filterDate2;
                                            }
                                            break;
                                        default:
                                            matches = true;
                                    }
                                    return matches ? index : -1;
                                }).filter(index => index !== -1);

                                // Если нет совпадающих элементов, исключаем элемент из результата
                                if (matchingIndices.length === 0) {
                                    return null;
                                }

                                // Создаем копию элемента с отфильтрованными массивами
                                const filteredItem = { ...item };

                                // Фильтруем plan_start
                                if (Array.isArray(itemTime)) {
                                    filteredItem.plan_start = {
                                        ...item.plan_start,
                                        v: itemTime.filter((_, index) => matchingIndices.includes(index))
                                    };

                                    // Фильтруем связанные поля
                                    const relatedFields = ['plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'name_by_acts', 'responsible', 'equipment', 'coexecutors'];
                                    relatedFields.forEach(fieldName => {
                                        if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                            if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                            filteredItem[fieldName] = {
                                                ...item[fieldName],
                                                v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                            };
                                        }
                                    });
                                } else {
                                    if (matchingIndices.length === 0) return null
                                }



                                return filteredItem;
                            }).filter(item => item !== null);
                        }
                        break;







                    case 'created_at':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.filter(item => {
                                if (!item.created_at) return false;
                                const itemDate = new Date(item.created_at);

                                switch (filter.operator) {
                                    case 'equals':
                                        return itemDate.getTime() === filterDate.getTime();
                                    case 'greater':
                                        return itemDate > filterDate;
                                    case 'less':
                                        return itemDate < filterDate;
                                    case 'between':
                                        if (!filter.value2) return true;
                                        const filterDate2 = new Date(filter.value2);
                                        return itemDate >= filterDate && itemDate <= filterDate2;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'modified_at':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.filter(item => {
                                if (!item.modified_at) return false;
                                const itemDate = new Date(item.modified_at);

                                switch (filter.operator) {
                                    case 'equals':
                                        return itemDate.getTime() === filterDate.getTime();
                                    case 'greater':
                                        return itemDate > filterDate;
                                    case 'less':
                                        return itemDate < filterDate;
                                    case 'between':
                                        if (!filter.value2) return true;
                                        const filterDate2 = new Date(filter.value2);
                                        return itemDate >= filterDate && itemDate <= filterDate2;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'stage':
                        if (filter.value && filter.value !== '') {
                            const filterStage = Number(filter.value);
                            if (!isNaN(filterStage)) {
                                filtered = filtered.map(item => {
                                    const itemStage = getValue(item.stage);
                                    if (itemStage === null) return null;
                                    if (Array.isArray(itemStage) && itemStage.length === 0) return null;
                                    // Определяем какие элементы stage соответствуют фильтру
                                    let matchingIndices = (Array.isArray(itemStage) ? itemStage : [itemStage]).map((stage, index) => {
                                        const numStage = parseFloat(stage);
                                        if (isNaN(numStage)) return -1;
                                        let matches = false;

                                        switch (filter.operator) {
                                            case 'equals':
                                                matches = numStage === filterStage;
                                                break;
                                            case 'greater':
                                                matches = numStage > filterStage;
                                                break;
                                            case 'less':
                                                matches = numStage < filterStage;
                                                break;
                                            default:
                                                matches = true;
                                        }
                                        return matches ? index : -1;
                                    })
                                        .filter(index => index !== -1);

                                    // Если нет совпадающих элементов, исключаем элемент из результата
                                    if (matchingIndices.length === 0) {
                                        return null;
                                    }

                                    // Создаем копию элемента с отфильтрованными массивами
                                    const filteredItem = { ...item };

                                    // Фильтруем stage
                                    if (Array.isArray(itemStage)) {
                                        filteredItem.stage = {
                                            ...item.stage,
                                            v: itemStage.filter((_, index) => matchingIndices.includes(index))
                                        };

                                        // Фильтруем связанные поля
                                        const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'closing', 'cost', 'comment', 'name_by_acts', 'responsible', 'equipment', 'coexecutors'];
                                        relatedFields.forEach(fieldName => {
                                            if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                                if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                                filteredItem[fieldName] = {
                                                    ...item[fieldName],
                                                    v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                                };
                                            }
                                        });
                                    } else {
                                        if (matchingIndices.length === 0) return null
                                    }



                                    return filteredItem;
                                }).filter(item => item !== null);
                            }
                        }
                        break;

                    case 'closing':
                        if (filter.value && filter.value !== '') {
                            const filterClosing = Number(filter.value);
                            if (!isNaN(filterClosing)) {
                                filtered = filtered.map(item => {
                                    const itemClosing = getValue(item.closing) || 0;
                                    if (itemClosing === null) return item;

                                    // Определяем какие элементы closing соответствуют фильтру
                                    let matchingIndices = (Array.isArray(itemClosing) ? itemClosing : [itemClosing]).map((closing, index) => {
                                        const numClosing = parseFloat(closing);
                                        if (isNaN(numClosing)) return -1;
                                        let matches = false;

                                        switch (filter.operator) {
                                            case 'equals':
                                                matches = numClosing === filterClosing;
                                                break;
                                            case 'greater':
                                                matches = numClosing > filterClosing;
                                                break;
                                            case 'less':
                                                matches = numClosing < filterClosing;
                                                break;
                                            default:
                                                matches = true;
                                        }
                                        return matches ? index : -1;
                                    })
                                        .filter(index => index !== -1);


                                    // Если нет совпадающих элементов, исключаем элемент из результата
                                    if (matchingIndices.length === 0) {
                                        return null;
                                    }

                                    // Создаем копию элемента с отфильтрованными массивами
                                    const filteredItem = { ...item };

                                    // Фильтруем closing
                                    if (Array.isArray(itemClosing)) {
                                        filteredItem.closing = {
                                            ...item.closing,
                                            v: itemClosing.filter((_, index) => matchingIndices.includes(index))
                                        };

                                        // Фильтруем связанные поля
                                        const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'cost', 'comment', 'name_by_acts', 'responsible', 'equipment', 'coexecutors'];
                                        relatedFields.forEach(fieldName => {
                                            if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                                if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                                filteredItem[fieldName] = {
                                                    ...item[fieldName],
                                                    v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                                };
                                            }
                                        });
                                    } else {
                                        if (matchingIndices.length === 0) return null
                                    }



                                    return filteredItem;
                                }).filter(item => item !== null);
                            }
                        }
                        break;

                    case 'cost':
                        if (filter.value && filter.value !== '') {
                            const filterCost = Number(filter.value);
                            if (!isNaN(filterCost)) {
                                filtered = filtered.map(item => {
                                    const itemCost = getValue(item.cost) || 0;
                                    // if (itemCost === null) return item;

                                    // Определяем какие элементы cost соответствуют фильтру
                                    let matchingIndices = (Array.isArray(itemCost) ? itemCost : [itemCost]).map((cost, index) => {
                                        const numCost = parseFloat(cost);
                                        if (isNaN(numCost)) return -1;
                                        let matches = false;

                                        switch (filter.operator) {
                                            case 'equals':
                                                matches = numCost === filterCost;
                                                break;
                                            case 'greater':
                                                matches = numCost > filterCost;
                                                break;
                                            case 'less':
                                                matches = numCost < filterCost;
                                                break;
                                            default:
                                                matches = true;
                                        }
                                        return matches ? index : -1;
                                    })
                                        .filter(index => index !== -1);

                                    console.log(item.fid, matchingIndices)
                                    // Если нет совпадающих элементов, исключаем элемент из результата
                                    if (matchingIndices.length === 0) {
                                        return null;
                                    }

                                    // Создаем копию элемента с отфильтрованными массивами
                                    const filteredItem = { ...item };

                                    // Фильтруем cost
                                    if (Array.isArray(itemCost)) {
                                        filteredItem.cost = {
                                            ...item.cost,
                                            v: itemCost.filter((_, index) => matchingIndices.includes(index))
                                        };

                                        // Фильтруем связанные поля
                                        const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'comment', 'name_by_acts', 'responsible', 'equipment', 'coexecutors'];
                                        relatedFields.forEach(fieldName => {
                                            if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                                if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                                filteredItem[fieldName] = {
                                                    ...item[fieldName],
                                                    v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                                };
                                            }
                                        });
                                    } else {
                                        if (matchingIndices.length === 0) return null
                                    }


                                    console.log(filteredItem)
                                    return filteredItem;
                                }).filter(item => item !== null);
                            }
                        }
                        break;

                    case 'comment':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchTerm = filter.value.toLowerCase().trim();
                            filtered = filtered.map(item => {
                                const itemComment = getValue(item.comment);
                                if (itemComment === null) return item;

                                // Определяем какие элементы comment соответствуют фильтру
                                let matchingIndices = (Array.isArray(itemComment) ? itemComment : [itemComment]).map((comment, index) => {
                                    const commentStr = String(comment || '').toLowerCase();
                                    let matches = false;

                                    switch (filter.operator) {
                                        case 'contains':
                                            matches = commentStr.includes(searchTerm);
                                            break;
                                        case 'not_contains':
                                            matches = !commentStr.includes(searchTerm);
                                            break;
                                        case 'equals':
                                            matches = commentStr === searchTerm;
                                            break;
                                        case 'not_equals':
                                            matches = commentStr !== searchTerm;
                                            break;
                                        default:
                                            matches = true;
                                    }
                                    return matches ? index : -1;
                                })
                                    .filter(index => index !== -1);


                                // Если нет совпадающих элементов, исключаем элемент из результата
                                if (matchingIndices.length === 0) {
                                    return null;
                                }

                                // Создаем копию элемента с отфильтрованными массивами
                                const filteredItem = { ...item };

                                // Фильтруем comment
                                if (Array.isArray(itemComment)) {
                                    filteredItem.comment = {
                                        ...item.comment,
                                        v: itemComment.filter((_, index) => matchingIndices.includes(index))
                                    };
                                    // Фильтруем связанные поля
                                    const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'name_by_acts', 'responsible', 'equipment', 'coexecutors'];
                                    relatedFields.forEach(fieldName => {
                                        if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                            if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                            filteredItem[fieldName] = {
                                                ...item[fieldName],
                                                v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                            };
                                        }
                                    });
                                } else {
                                    if (matchingIndices.length === 0) return null
                                }



                                return filteredItem;
                            }).filter(item => item !== null);
                        }
                        break;

                    case 'name_by_acts':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchTerm = filter.value.toLowerCase().trim();
                            filtered = filtered.map(item => {
                                const itemNameByActs = getValue(item.name_by_acts);
                                if (itemNameByActs === null) return item;

                                // Определяем какие элементы name_by_acts соответствуют фильтру
                                let matchingIndices = (Array.isArray(itemNameByActs) ? itemNameByActs : [itemNameByActs]).map((nameByActs, index) => {
                                    const nameByActsStr = String(nameByActs || '').toLowerCase();
                                    let matches = false;

                                    switch (filter.operator) {
                                        case 'contains':
                                            matches = nameByActsStr.includes(searchTerm);
                                            break;
                                        case 'not_contains':
                                            matches = !nameByActsStr.includes(searchTerm);
                                            break;
                                        case 'equals':
                                            matches = nameByActsStr === searchTerm;
                                            break;
                                        case 'not_equals':
                                            matches = nameByActsStr !== searchTerm;
                                            break;
                                        default:
                                            matches = true;
                                    }
                                    return matches ? index : -1;
                                })
                                    .filter(index => index !== -1);


                                // Если нет совпадающих элементов, исключаем элемент из результата
                                if (matchingIndices.length === 0) {
                                    return null;
                                }

                                // Создаем копию элемента с отфильтрованными массивами
                                const filteredItem = { ...item };

                                // Фильтруем name_by_acts
                                if (Array.isArray(itemNameByActs)) {
                                    filteredItem.name_by_acts = {
                                        ...item.name_by_acts,
                                        v: itemNameByActs.filter((_, index) => matchingIndices.includes(index))
                                    };
                                    // Фильтруем связанные поля
                                    const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'responsible', 'equipment', 'coexecutors'];
                                    relatedFields.forEach(fieldName => {
                                        if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                            if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                            filteredItem[fieldName] = {
                                                ...item[fieldName],
                                                v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                            };
                                        }
                                    });
                                } else {
                                    if (matchingIndices.length === 0) return null
                                }



                                return filteredItem;
                            }).filter(item => item !== null);
                        }
                        break;

                    case 'responsible':
                        if (filter.value && filter.value.length > 0) {
                            const responsibleIds = filter.value.map(c => c.id);
                            filtered = filtered.map(item => {
                                const itemResponsible = getValue(item.responsible);
                                if (itemResponsible === null) return null;
                                if (Array.isArray(itemResponsible) && itemResponsible.length === 0) return null;
                                // Определяем какие элементы coexecutors соответствуют фильтру
                                let matchingIndices = (Array.isArray(itemResponsible) ? itemResponsible : [itemResponsible]).map((item, index) => {
                                    let hasMatch = false;
                                    if (filter.logic === 'and') {
                                        // И - все выбранные значения должны присутствовать в подмассиве
                                        hasMatch = responsibleIds.every(id => item.includes(id));
                                    } else {
                                        // ИЛИ - любое из выбранных значений в подмассиве
                                        hasMatch = item.some(id => responsibleIds.includes(id));
                                    }

                                    return hasMatch ? index : -1;
                                })
                                    .filter(index => index !== -1);


                                // Если нет совпадающих элементов, исключаем элемент из результата
                                if (matchingIndices.length === 0) {
                                    return null;
                                }

                                // Создаем копию элемента с отфильтрованными массивами
                                const filteredItem = { ...item };

                                // Фильтруем coexecutors
                                if (Array.isArray(itemResponsible)) {
                                    // Для вложенных массивов фильтруем подмассивы
                                    filteredItem.responsible = {
                                        ...item.responsible,
                                        v: itemResponsible.filter((_, index) => matchingIndices.includes(index))
                                    };

                                    // Фильтруем связанные поля
                                    const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'name_by_acts', 'coexecutors', 'equipment'];
                                    relatedFields.forEach(fieldName => {
                                        if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                            if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                            filteredItem[fieldName] = {
                                                ...item[fieldName],
                                                v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                            };
                                        }
                                    });


                                } else {
                                    if (matchingIndices.length === 0) return null
                                }



                                return filteredItem;
                            }).filter(item => item !== null);
                        }
                        break;

                    case 'coexecutors':
                        if (filter.value && filter.value.length > 0) {
                            const coexecutorIds = filter.value.map(c => c.id);
                            filtered = filtered.map(item => {
                                const itemCoexecutors = getArrayValue(item.coexecutors);
                                if (itemCoexecutors.length === 0) return item;

                                // Определяем какие элементы coexecutors соответствуют фильтру
                                let matchingIndices = (Array.isArray(itemCoexecutors[0]) ? itemCoexecutors : [itemCoexecutors]).map((subArray, index) => {
                                    if (!Array.isArray(subArray)) return -1;

                                    let hasMatch = false;
                                    if (filter.logic === 'and') {
                                        // И - все выбранные значения должны присутствовать в подмассиве
                                        hasMatch = coexecutorIds.every(id => subArray.includes(id));
                                    } else {
                                        // ИЛИ - любое из выбранных значений в подмассиве
                                        hasMatch = subArray.some(id => coexecutorIds.includes(id));
                                    }

                                    return hasMatch ? index : -1;
                                })
                                    .filter(index => index !== -1);


                                // Если нет совпадающих элементов, исключаем элемент из результата
                                if (matchingIndices.length === 0) {
                                    return null;
                                }

                                // Создаем копию элемента с отфильтрованными массивами
                                const filteredItem = { ...item };

                                // Фильтруем coexecutors
                                if (Array.isArray(itemCoexecutors[0])) {
                                    // Для вложенных массивов фильтруем подмассивы
                                    filteredItem.coexecutors = {
                                        ...item.coexecutors,
                                        v: itemCoexecutors.filter((_, index) => matchingIndices.includes(index))
                                    };

                                    // Фильтруем связанные поля
                                    const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'name_by_acts', 'responsible', 'equipment'];
                                    relatedFields.forEach(fieldName => {
                                        if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                            if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                            filteredItem[fieldName] = {
                                                ...item[fieldName],
                                                v: item[fieldName].v.filter((_, index) => matchingIndices.includes(index))
                                            };
                                        }
                                    });
                                } else {
                                    if (matchingIndices.length === 0) return null
                                }



                                return filteredItem;
                            }).filter(item => item !== null);
                        }
                        break;

                    case 'equipment':
                        if (filter.value && filter.value.length > 0) {
                            const equipmentIds = filter.value.map(e => Number(e.id));
                            filtered = filtered.map(item => {
                                const itemEquipment = getArrayValue(item.equipment);
                                if (itemEquipment.length === 0) return null;

                                // Определяем какие элементы equipment соответствуют фильтру
                                let matchingEquipmentIndices = [];
                                if (filter.logic === 'and') {
                                    // И - все выбранные значения должны присутствовать
                                    matchingEquipmentIndices = itemEquipment
                                        .map((id, index) => equipmentIds.includes(Number(id)) ? index : -1)
                                        .filter(index => index !== -1);
                                } else {
                                    // ИЛИ - любое из выбранных значений
                                    matchingEquipmentIndices = itemEquipment
                                        .map((id, index) => equipmentIds.includes(Number(id)) ? index : -1)
                                        .filter(index => index !== -1);
                                }

                                // Если нет совпадающих элементов, исключаем элемент из результата
                                console.log(item.id, matchingEquipmentIndices)
                                if (matchingEquipmentIndices.length === 0) {
                                    return null;
                                }

                                // Создаем копию элемента с отфильтрованными массивами
                                const filteredItem = { ...item };

                                // Фильтруем equipment
                                filteredItem.equipment = {
                                    ...item.equipment,
                                    v: itemEquipment.filter((_, index) => matchingEquipmentIndices.includes(index))
                                };



                                // Фильтруем связанные поля
                                const relatedFields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'name_by_acts', 'responsible', 'coexecutors'];
                                relatedFields.forEach(fieldName => {
                                    if (item[fieldName] && Array.isArray(item[fieldName].v)) {
                                        if (fieldName === 'coexecutors' && !Array.isArray(item[fieldName].v[0])) return
                                        filteredItem[fieldName] = {
                                            ...item[fieldName],
                                            v: item[fieldName].v.filter((_, index) => matchingEquipmentIndices.includes(index))
                                        };
                                    }
                                });

                                return filteredItem;
                            }).filter(item => item);
                            console.log(filtered)
                        }
                        break;

                    case 'creator':
                        if (filter.value && filter.value.length > 0) {
                            const creatorIds = filter.value.map(c => c.id);
                            filtered = filtered.filter(item => {
                                if (!item.creator) return false;

                                if (filter.logic === 'and') {
                                    // И - все выбранные значения должны присутствовать
                                    return creatorIds.includes(item.creator);
                                } else {
                                    // ИЛИ - любое из выбранных значений
                                    return creatorIds.includes(item.creator);
                                }
                            });
                        }
                        break;

                    case 'modifier':
                        if (filter.value && filter.value.length > 0) {
                            const modifierIds = filter.value.map(m => m.id);
                            filtered = filtered.filter(item => {
                                if (!item.modifier) return false;

                                if (filter.logic === 'and') {
                                    // И - все выбранные значения должны присутствовать
                                    return modifierIds.includes(item.modifier);
                                } else {
                                    // ИЛИ - любое из выбранных значений
                                    return modifierIds.includes(item.modifier);
                                }
                            });
                        }
                        break;
                }
            });
        }

        // Сортировка
        if (sortBy) {
            filtered.sort((a, b) => {
                let aValue, bValue;

                switch (sortBy) {
                    case 'fid':
                        aValue = a.fid || 0;
                        bValue = b.fid || 0;
                        break;
                    case 'name':
                        aValue = transformers.get(a.object_id)?.name || '';
                        bValue = transformers.get(b.object_id)?.name || '';
                        break;
                    case 'start':
                        // Сортируем по плановому началу, если нет - по фактическому
                        const getDateValue = (dateField) => {
                            if (!dateField) return 0;
                            const value = dateField.v !== undefined ? dateField.v : dateField;
                            if (!value) return 0;
                            if (Array.isArray(value)) {
                                const validDates = value
                                    .map(d => {
                                        try {
                                            return new Date(d).getTime();
                                        } catch {
                                            return 0;
                                        }
                                    })
                                    .filter(time => !isNaN(time) && time > 0);
                                return validDates.length > 0 ? Math.max(...validDates) : 0;
                            }
                            try {
                                const time = new Date(value).getTime();
                                return isNaN(time) ? 0 : time;
                            } catch {
                                return 0;
                            }
                        };

                        aValue = a.plan_start ? getDateValue(a.plan_start) : getDateValue(a.fact_start);
                        bValue = b.plan_start ? getDateValue(b.plan_start) : getDateValue(b.fact_start);
                        break;
                    case 'end':
                        // Сортируем по плановому завершению, если нет - по фактическому
                        const getDateValueEnd = (dateField) => {
                            if (!dateField) return 0;
                            const value = dateField.v !== undefined ? dateField.v : dateField;
                            if (!value) return 0;
                            if (Array.isArray(value)) {
                                const validDates = value
                                    .map(d => {
                                        try {
                                            return new Date(d).getTime();
                                        } catch {
                                            return 0;
                                        }
                                    })
                                    .filter(time => !isNaN(time) && time > 0);
                                return validDates.length > 0 ? Math.max(...validDates) : 0;
                            }
                            try {
                                const time = new Date(value).getTime();
                                return isNaN(time) ? 0 : time;
                            } catch {
                                return 0;
                            }
                        };

                        aValue = a.plan_end ? getDateValueEnd(a.plan_end) : getDateValueEnd(a.fact_end);
                        bValue = b.plan_end ? getDateValueEnd(b.plan_end) : getDateValueEnd(b.fact_end);
                        break;
                    case 'stage':
                        const getStageValue = (stageField) => {
                            if (!stageField) return 0;
                            const value = stageField.v !== undefined ? stageField.v : stageField;
                            if (!value) return 0;
                            const stages = Array.isArray(value) ? value : [value];
                            const validStages = stages.filter(s => typeof s === 'number' && !isNaN(s));
                            return validStages.length > 0 ? Math.min(...validStages) : 0;
                        };
                        aValue = getStageValue(a.stage);
                        bValue = getStageValue(b.stage);
                        break;
                    case 'closing':
                        const getNumericValue = (field) => {
                            if (!field) return 0;
                            const value = field.v !== undefined ? field.v : field;
                            if (!value) return 0;
                            const values = Array.isArray(value) ? value : [value];
                            return values.reduce((acc, item) => {
                                const num = parseFloat(item);
                                return acc + (isNaN(num) ? 0 : num);
                            }, 0);
                        };
                        aValue = getNumericValue(a.closing);
                        bValue = getNumericValue(b.closing);
                        break;
                    case 'cost':
                        const getCostValue = (field) => {
                            if (!field) return 0;
                            const value = field.v !== undefined ? field.v : field;
                            if (!value) return 0;
                            const values = Array.isArray(value) ? value : [value];
                            return values.reduce((acc, item) => {
                                const num = parseFloat(item);
                                return acc + (isNaN(num) ? 0 : num);
                            }, 0);
                        };
                        aValue = getCostValue(a.cost);
                        bValue = getCostValue(b.cost);
                        break;
                    case 'comment':
                        const getCommentLength = (commentField) => {
                            if (!commentField) return 0;
                            const value = commentField.v !== undefined ? commentField.v : commentField;
                            if (!value) return 0;
                            if (Array.isArray(value)) {
                                return value.reduce((acc, item) => {
                                    return acc + (typeof item === 'string' ? item.length : 0);
                                }, 0);
                            }
                            return typeof value === 'string' ? value.length : 0;
                        };
                        aValue = getCommentLength(a.comment);
                        bValue = getCommentLength(b.comment);
                        break;
                    case 'responsible':
                        const getResponsibleValue = (responsibleField) => {
                            if (!responsibleField) return '';
                            const value = responsibleField.v !== undefined ? responsibleField.v : responsibleField;
                            if (!value) return '';
                            const responsibles = Array.isArray(value) ? value : [value];
                            return responsibles.map(r => responsible[r] || '').join(',').toLowerCase();
                        };
                        aValue = getResponsibleValue(a.responsible);
                        bValue = getResponsibleValue(b.responsible);
                        break;
                    case 'coexecutors':
                        const getCoexecutorsValue = (coexecutorsField) => {
                            if (!coexecutorsField) return '';
                            const value = coexecutorsField.v !== undefined ? coexecutorsField.v : coexecutorsField;
                            if (!value) return '';

                            // Если это не массив, возвращаем пустую строку
                            if (!Array.isArray(value)) return '';

                            // Если массив пустой
                            if (value.length === 0) return '';

                            // Проверяем, является ли первый элемент массивом (вложенная структура)
                            if (Array.isArray(value[0])) {
                                // Вложенный массив - обрабатываем каждый подмассив
                                return value.map(subArray => {
                                    if (!Array.isArray(subArray)) return '';
                                    return subArray.map(item => responsible[item]).filter(name => name).join(', ');
                                }).filter(group => group).join('; ');
                            } else {
                                // Простой массив
                                return value.map(item => responsible[item]).filter(name => name).join(', ');
                            }
                        };
                        aValue = getCoexecutorsValue(a.coexecutors).toLowerCase();
                        bValue = getCoexecutorsValue(b.coexecutors).toLowerCase();
                        break;
                    case 'equipment':
                        const getEquipmentValue = (equipmentField) => {
                            if (!equipmentField) return '';
                            const value = equipmentField.v !== undefined ? equipmentField.v : equipmentField;
                            if (!value || !Array.isArray(value)) return '';
                            return value.map(e => equipment[e] || '').join(', ').toLowerCase();
                        };
                        aValue = getEquipmentValue(a.equipment);
                        bValue = getEquipmentValue(b.equipment);
                        break;
                    case 'created':
                        const getCreatedValue = (createdField) => {
                            if (!createdField) return 0;
                            try {
                                const time = new Date(createdField).getTime();
                                return isNaN(time) ? 0 : time;
                            } catch {
                                return 0;
                            }
                        };
                        aValue = getCreatedValue(a.created_at);
                        bValue = getCreatedValue(b.created_at);
                        break;
                    case 'modified':
                        const getModifiedValue = (modifiedField) => {
                            if (!modifiedField) return 0;
                            try {
                                const time = new Date(modifiedField).getTime();
                                return isNaN(time) ? 0 : time;
                            } catch {
                                return 0;
                            }
                        };
                        aValue = getModifiedValue(a.modified_at);
                        bValue = getModifiedValue(b.modified_at);
                        break;
                    default:
                        return 0;
                }

                // Обработка значений null/undefined
                if (aValue === null || aValue === undefined) aValue = sortBy === 'stage' || sortBy === 'fid' ? 0 : '';
                if (bValue === null || bValue === undefined) bValue = sortBy === 'stage' || sortBy === 'fid' ? 0 : '';

                // Для числовых значений
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    const comparison = aValue - bValue;
                    return sortDirection === 'desc' ? -comparison : comparison;
                }

                // Для строковых значений
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const comparison = aValue.localeCompare(bValue, 'ru');
                    return sortDirection === 'desc' ? -comparison : comparison;
                }

                return 0;
            });
        }


        console.log(filtered)
        return filtered.map(item => ({
            ...item,
            object: transformers.get(item.object_id),
            rowSpan: { ...item, ...editedRows[item.id]?.changes }?.equipment.v?.length === 0 ? 1 : { ...item, ...editedRows[item.id]?.changes }?.equipment.v?.length + 1,
            // equipment: item.equipment.v?.map(id => equipment[id]) || [],
            // responsible: Array.isArray(item.responsible.v) ? item.responsible.v.map(r => responsible[r]) : item.responsible.v ? responsible[item.responsible.v] : null,
            // coexecutors: item.coexecutors.v?.map(c => Array.isArray(c) ? c.map(id => responsible[id]) : c ? responsible[c] : null) || [],
            // creator: item.creator ? responsible[item.creator] : null,
            // modifier: item.modifier ? responsible[item.modifier] : null
        }));
    }, [user.id, equipment, responsible, transformers]);

    // Состояния для сортировки (объявляем здесь, чтобы они были доступны в useEffect ниже)
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

    // Обновляем отфильтрованные данные при изменении исходных данных или параметров
    useEffect(() => {
        const filtered = filterAndSortData(data, editedRows, searchQuery, selectedProjects, advancedFilters, sortBy, sortDirection);
        setFilteredData(filtered);
    }, [data, searchQuery, selectedProjects, advancedFilters, sortBy, sortDirection, filterAndSortData, transformers, editedRows]);


    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            let response;
            if (user.roles.some(role => ['admin', 'project_manager'].includes(role))) {
                response = await $authHost.get('/apig/reestr', {
                    params: {
                        is_active: true
                    }
                });
            } else {
                response = await $authHost.get('/apig/reestr/by-responsible/' + user.id);
            }
            const { success, data } = response.data;
            setData(success ? data : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            setData([]);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchData()
    }, [user]);


    useEffect(() => {
        const fetchResponsible = async () => {
            try {
                const response = await $authHost.get('/apig/users');
                const { success, data } = response.data;

                const users = {}

                if (success) {
                    data.forEach(item => {
                        users[item.id] = item.display_name
                    })
                }

                setResponsible(users)
            } catch (e) {
                setResponsible({})
                console.log(e)
                setError(e.message);
            }
        };
        fetchResponsible();
    }, []);




    useEffect(() => {
        const fetchEquipment = async () => {
            try {
                const response = await $authHost.get('/apig/reestr/equipment');
                const { success, data } = response.data;
                const equipment = {}

                if (success) {
                    data.forEach(item => {
                        equipment[item.id] = item.name
                    })
                }
                setEquipment(equipment)
            } catch (e) {
                setEquipment({})
                console.log(e)
                setError(e.message);
            }
        };
        fetchEquipment();
    }, []);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await $authHost.get('/apig/reestr/projects');
                const { success, data } = response.data;
                setProjects(success ? data : []);
            } catch (e) {
                setProjects([])
                console.log(e)
                setError(e.message);
            }
        };
        fetchProjects();
    }, []);

    useEffect(() => {
        dispatch(fetchState())
    }, []);

    const activeProjects = useMemo(() => {
        return (projects || []).filter(project => data.some(item => item.project_id === project.id));
    }, [projects, data]);


    // Функция для подготовки данных для группировки
    const prepareGroupedData = useCallback((filteredData, editedRows, selectedProjects, projectsList) => {
        // Если не выбран ни один КДП, показываем все данные без группировки
        if (!selectedProjects || selectedProjects.length === 0) {
            return {
                groupCounts: [],
                groupCountsRows: [],
                groupedData: [],
            };
        }
        // Группируем по КДП
        const groupedByKdp = {};
        console.log(filteredData, editedRows)
        filteredData.forEach(item => {
            const itemProjectId = item.project_id;
            const projectName = projectsList.find(p => p.id === itemProjectId)?.name || 'Без проекта';

            if (!groupedByKdp[projectName]) {
                groupedByKdp[projectName] = [];
            }
            groupedByKdp[projectName].push(item);
        });

        // Сортируем группы по названию КДП
        const sortedGroups = Object.keys(groupedByKdp).sort();



        // Создаем плоский массив данных с информацией о группе
        const groupedData = [];
        const groupCounts = [];
        const groupCountsRows = [];

        sortedGroups.forEach((groupName, groupIndex) => {
            const groupItems = groupedByKdp[groupName];

            // Правильный подсчет: сумма всех rowSpan в группе
            const counts = groupItems.reduce((a, v) => a + (v.rowSpan || 1), 0)

            groupCounts.push(counts);
            groupCountsRows.push(groupItems.length)

            groupItems.forEach(item => {
                const itemRowSpan = item.rowSpan || 1;
                // Создаем нужное количество строк для каждого элемента оборудования
                for (let index = 0; index < itemRowSpan; index++) {
                    groupedData.push({
                        ...item,
                        group: groupIndex,
                        groupName: groupName,
                        equipmentIndex: index
                    });
                }
            });
        });



        // Добавляем пустые элементы в конец для принудительного рендеринга
        const paddingItems = Array.from({ length: sortedGroups.length }, (_, index) => ({
            id: `padding-${index}`,
            fid: groupedData.length + index + 1,
            group: groupedData.length > 0 ? groupedData[groupedData.length - 1].group : 0,
            groupName: groupedData.length > 0 ? groupedData[groupedData.length - 1].groupName : 'Все объекты',
            isPadding: true
        }));

        return { groupCounts, groupCountsRows, groupedData: [...groupedData, ...paddingItems] };
    }, []);

    // Обновляем группированные данные при изменении отфильтрованных данных
    useEffect(() => {
        const grouped = prepareGroupedData(filteredData, editedRows, selectedProjects, activeProjects);

        setGroupedData(grouped);
    }, [filteredData, selectedProjects, activeProjects, prepareGroupedData, editedRows]);



    // Обработчик клика вне области редактирования
    // useEffect(() => {
    //     const handleClickOutside = (event) => {
    //         // Если мы находимся в режиме редактирования
    //         if (editing.id && editing.field) {
    //             // Проверяем, что клик не по элементу редактирования
    //             const editingElement = event.target.closest('[data-editing="true"]');
    //             if (!editingElement) {
    //                 // Выходим из режима редактирования
    //                 setEditing({ id: null, field: null });
    //             }
    //         }
    //     };

    //     // Добавляем обработчик только если мы в режиме редактирования
    //     if (editing.id && editing.field) {
    //         document.addEventListener('mousedown', handleClickOutside);
    //     }

    //     return () => {
    //         document.removeEventListener('mousedown', handleClickOutside);
    //     };
    // }, [editing]);



    // Состояние для управления видимостью столбцов
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const stored = storedSettings.visibleColumns;
        if (stored) {
            // Проверяем, что все текущие столбцы присутствуют в сохраненных настройках
            const validStoredColumns = {};
            columns.forEach(col => {
                validStoredColumns[col.id] = stored[col.id] !== undefined ? stored[col.id] : true;
            });
            return validStoredColumns;
        }
        // Если нет сохраненных настроек, показываем все столбцы
        return columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
    });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [columnWidths, setColumnWidths] = useState(storedSettings.columnWidths || {});
    const [resizingColumn, setResizingColumn] = useState(null);
    const [resizeStartX, setResizeStartX] = useState(0);
    const [resizeStartWidth, setResizeStartWidth] = useState(0);

    // Сохранение настроек в localStorage при изменении
    useEffect(() => {
        storeSettings({ darkMode });
    }, [darkMode]);

    useEffect(() => {
        storeSettings({ selectedProjects });
    }, [selectedProjects]);

    useEffect(() => {
        storeSettings({ visibleColumns });
    }, [visibleColumns]);

    useEffect(() => {
        storeSettings({ showControlMenu });
    }, [showControlMenu]);

    // Функция для получения ширины столбца
    const getColumnWidth = (columnId) => {
        if (columnWidths[columnId]) {
            return columnWidths[columnId];
        }
        const column = columns.find(col => col.id === columnId);
        return column ? column.width : 150;
    };

    // Обработчики для изменения размера столбцов
    const handleResizeStart = (e, columnId) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = getColumnWidth(columnId);
        setResizingColumn(columnId);
        setResizeStartX(startX);
        setResizeStartWidth(startWidth);
    };

    useEffect(() => {
        if (!resizingColumn) return;

        const handleMouseMove = (e) => {
            const diff = e.clientX - resizeStartX;
            const newWidth = Math.max(30, resizeStartWidth + diff); // Минимальная ширина 30px
            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizingColumn, resizeStartX, resizeStartWidth]);

    // Сохранение ширины столбцов в localStorage
    useEffect(() => {
        storeSettings({ columnWidths });
    }, [columnWidths]);

    const handleEditedField = ({ rowId, field, value, action = 'change', isValueChanged, shouldExitEditMode = true, deleted = null, equipment = null, relatedFields: providedRelatedFields = null }) => {
        console.log(rowId, field, value, isValueChanged, deleted, equipment)
        if (isValueChanged) {

            // Обновляем данные
            // setData(prev =>
            //     prev.map(p => (
            //         p.id === rowId ? { ...p, [field]: value } : p
            //     ))
            // )

            let clearValue;
            let relatedFields = providedRelatedFields || {}
            let existingRow = data.find(row => row.id === rowId)
            existingRow = {
                ...existingRow,
                ...editedRows[rowId]?.changes
            }
            console.log(existingRow)
            switch (field) {
                case 'equipment':
                    // Если переданы relatedFields, значит делаем перемещение - просто заменяем весь массив
                    if (providedRelatedFields && Object.keys(providedRelatedFields).length > 0) {
                        clearValue = { 'v': value };
                    } else {
                        clearValue = deleted ? { 'v': value } : { 'v': [...existingRow.equipment.v, ...value.filter(v => !existingRow.equipment.v.includes(v))] }
                    }
                    const changeCount = value.length - existingRow.equipment.v.length
                    let deletedIndex = null
                    if (deleted) {
                        deletedIndex = existingRow.equipment.v?.findIndex(v => v === deleted)
                    }

                    // const closing = changeCount <= 0 ? existingRow.closing.v.slice(0, value.length) : [...existingRow.closing.v, ...Array.from({ length: changeCount }, () => null)]
                    // const cost = changeCount <= 0 ? existingRow.cost.v.slice(0, value.length) : [...existingRow.cost.v, ...Array.from({ length: changeCount }, () => null)]
                    // const comment = changeCount <= 0 ? existingRow.comment.v.slice(0, value.length) : [...existingRow.comment.v, ...Array.from({ length: changeCount }, () => null)]
                    // Если переданы relatedFields, не пересчитываем их автоматически
                    if (providedRelatedFields && Object.keys(providedRelatedFields).length > 0) {
                        // Используем переданные relatedFields как есть
                    } else if (changeCount !== 0) {
                        const fields = ['name_by_acts', 'plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'responsible', 'coexecutors']
                        // const checkIsArray = [0, 1, 2, 3, 4, 8]
                        fields.forEach((field, index) => {
                            if (!Array.isArray(existingRow[field]?.v)) return
                            if (field === 'coexecutors' && existingRow[field].v && !Array.isArray(existingRow[field].v[0])) return
                            const newValue = changeCount <= 0
                                ? deletedIndex !== null
                                    ? [...existingRow[field].v.slice(0, deletedIndex), ...existingRow[field].v.slice(deletedIndex + 1)]
                                    : existingRow[field].v.slice(0, value.length)
                                : [...existingRow[field].v, ...Array.from({ length: changeCount }, () => null)]
                            relatedFields = {
                                ...relatedFields,
                                [field]: {
                                    'v': newValue.length === 0 ? [null] : newValue
                                }
                            }
                        })
                        // relatedFields = {
                        //     'closing': {
                        //         'v': closing.length === 0 ? [null] : closing
                        //     },
                        //     'cost': {
                        //         'v': cost.length === 0 ? [null] : cost
                        //     },
                        //     'comment': {
                        //         'v': comment.length === 0 ? [null] : comment
                        //     }
                        // }
                        // if (Array.isArray(existingRow.responsible.v)) {
                        //     const responsible = changeCount <= 0 ? existingRow.responsible.v.slice(0, value.length) : [...existingRow.responsible.v, ...Array.from({ length: changeCount }, () => null)]
                        //     relatedFields = {
                        //         ...relatedFields,
                        //         'responsible': {
                        //             'v': responsible.length === 0 ? [null] : responsible
                        //         }
                        //     }
                        // }`
                    }
                    break;
                case 'plan_start':
                case 'plan_end':
                case 'fact_start':
                case 'fact_end':
                case 'responsible':
                case 'coexecutors':
                case 'stage':
                    if (action === 'change') {
                        if (equipment) {
                            const index = existingRow.equipment.v?.findIndex(e => e === equipment)
                            if (index !== -1) {
                                const newValue = [...existingRow[field].v]
                                newValue[index] = value.id || value || null
                                clearValue = { 'v': newValue }
                            }
                        } else {
                            clearValue = { 'v': value }
                        }
                    } else if (action === 'multiple') {
                        if (field === 'coexecutors') {
                            if (Array.isArray(existingRow[field].v) && Array.isArray(existingRow[field].v[0])) {
                                clearValue = { 'v': existingRow[field].v[0].map(v => v.id || v || null) }
                            } else {
                                clearValue = { 'v': Array.from({ length: existingRow.equipment.v.length }, () => existingRow[field].v) }
                            }
                        } else {
                            if (Array.isArray(existingRow[field].v)) {
                                clearValue = { 'v': existingRow[field].v[0] || null }
                            } else {
                                clearValue = { 'v': Array.from({ length: existingRow.equipment.v.length }, () => existingRow[field].v) }
                            }
                        }
                    }
                    break;


                case 'closing':
                case 'cost':
                case 'comment':
                    if (equipment) {
                        const index = existingRow.equipment.v?.findIndex(e => e === equipment)
                        if (index !== -1) {
                            const newValue = [...existingRow[field].v]
                            newValue[index] = value
                            clearValue = { 'v': newValue }
                        } else {
                            clearValue = { 'v': [value] }
                        }
                    } else {
                        clearValue = { 'v': [value] }
                    }
                    break;
                default:
                    clearValue = { 'v': value }

            }

            // Записываем изменение в editedRows
            setEditedRows(prev => {
                if (prev[rowId]) {
                    return {
                        ...prev,
                        [rowId]: { ...prev[rowId], changes: { ...prev[rowId].changes, [field]: clearValue, ...relatedFields } }
                    }
                } else {
                    return { ...prev, [rowId]: { id: rowId, changes: { [field]: clearValue, ...relatedFields }, originalData: data.find(item => item.id === rowId) } }
                }
                // const existingRow = prev.find(row => row.id === rowId);
                // if (existingRow) {
                //     // Если строка уже есть, обновляем поле
                //     return prev.map(row =>
                //         row.id === rowId
                //             ? { ...row, changes: { ...row.changes, [field]: clearValue } }
                //             : row
                //     );
                // } else {
                //     // Если строки нет, добавляем новую
                //     return [...prev, {
                //         id: rowId,
                //         changes: { [field]: clearValue },
                //         originalData: data.find(item => item.id === rowId)
                //     }];
                // }
            });
        }

        // Сбрасываем состояние редактирования только если это явно запрошено
        if (shouldExitEditMode) {
            setEditing({ id: null, field: null })
        }
    }

    useEffect(() => { console.log(editedRows) }, [editedRows])

    const handleChangeEdititng = ({ id, field, index }) => {
        setEditing({ id, field, index })
    }

    // Функция для перемещения equipment и связанных полей
    const handleMoveEquipment = (rowId, currentIndex, direction) => {
        // direction: 'up' или 'down'
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        // Получаем актуальные данные строки
        let existingRow = data.find(row => row.id === rowId);
        existingRow = {
            ...existingRow,
            ...editedRows[rowId]?.changes
        };

        const equipmentArray = existingRow.equipment?.v || [];

        // Проверяем границы массива
        if (newIndex < 0 || newIndex >= equipmentArray.length) {
            return;
        }

        // Связанные поля, которые нужно перемещать вместе с equipment
        const relatedFieldsNames = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'name_by_acts', 'responsible', 'coexecutors'];

        // Создаем новые массивы с перемещенными элементами
        const newEquipment = [...equipmentArray];
        [newEquipment[currentIndex], newEquipment[newIndex]] = [newEquipment[newIndex], newEquipment[currentIndex]];

        const relatedFieldsUpdates = {};

        relatedFieldsNames.forEach(fieldName => {
            const fieldValue = existingRow[fieldName]?.v;
            if (Array.isArray(fieldValue)) {
                // Проверяем специальный случай для coexecutors
                if (fieldName === 'coexecutors' && !Array.isArray(fieldValue[0])) {
                    return;
                }

                const newFieldValue = [...fieldValue];
                // Убеждаемся, что массив достаточно длинный для обоих индексов
                const maxIndex = Math.max(currentIndex, newIndex);
                while (newFieldValue.length <= maxIndex) {
                    newFieldValue.push(null);
                }

                // Перемещаем элементы только если оба индекса валидны
                if (currentIndex >= 0 && newIndex >= 0 && currentIndex < newFieldValue.length && newIndex < newFieldValue.length) {
                    [newFieldValue[currentIndex], newFieldValue[newIndex]] = [newFieldValue[newIndex], newFieldValue[currentIndex]];
                    relatedFieldsUpdates[fieldName] = { 'v': newFieldValue };
                }
            }
        });

        // Сохраняем изменения
        handleEditedField({
            rowId: rowId,
            field: 'equipment',
            value: newEquipment,
            isValueChanged: true,
            shouldExitEditMode: false,
            relatedFields: relatedFieldsUpdates
        });
    }

    const handleOpenCreateDialog = (projectId = null) => {
        setSelectedObject(null);
        setSelectedProjectId(projectId)
        setObjectDialogOpen(true);
        // // Передаем выбранный KDP ID в диалог
        // if (selectedKdpId) {
        //     // Сохраняем выбранный KDP ID для передачи в диалог
        //     setSelectedKdp([selectedKdpId]);
        // } else {
        //     // Очищаем выбранный KDP если не передан
        //     setSelectedKdp([]);
        // }
    };

    const handleOpenEditDialog = (object) => {
        setSelectedObject(object);
        setSelectedProjectId(object.project?.id)
        setObjectDialogOpen(true);
    };

    const handleCloseObjectDialog = (update = false) => {
        if (update) {
            fetchData()
        }
        setSelectedProjectId(null)
        setObjectDialogOpen(false);
        setSelectedObject(null);
        // // Очищаем выбранный KDP при закрытии диалога
        // setSelectedKdp([]);
    };

    const handleOpenHistoryDialog = (object) => {
        setSelectedObject(object);
        setHistoryDialogOpen(true);
    };

    const handleCloseHistoryDialog = () => {
        setHistoryDialogOpen(false);
        setSelectedObject(null);
    };

    const handleOpenCommentDialog = (object) => {
        // Получаем исходный объект из data, а не из filteredData
        // чтобы избежать потери комментариев при применении фильтров
        const originalObject = data.find(item => item.id === object.id);
        if (!originalObject) {
            console.error('Object not found in data:', object.id);
            return;
        }

        // Применяем изменения из editedRows к исходному объекту
        const objectWithChanges = {
            ...originalObject,
            ...editedRows[originalObject.id]?.changes
        };

        setCommentEditObject(objectWithChanges);
        setCommentDialogOpen(true);
    };

    const handleCloseCommentDialog = () => {
        setCommentDialogOpen(false);
        setCommentEditObject(null);
    };

    const handleSaveComments = (newComments) => {
        if (commentEditObject) {
            // Получаем исходный объект из data для правильного обновления массива комментариев
            const originalObject = data.find(item => item.id === commentEditObject.id);
            if (!originalObject) {
                console.error('Object not found in data:', commentEditObject.id);
                return;
            }

            // Получаем текущее состояние объекта с учетом изменений из editedRows
            const existingRow = {
                ...originalObject,
                ...editedRows[commentEditObject.id]?.changes
            };

            // Определяем, нужно ли обновлять массив комментариев или это единичное значение
            const equipmentArray = existingRow.equipment?.v || [];
            let clearValue;

            if (equipmentArray.length > 0) {
                // Если есть оснащение, обновляем массив комментариев
                // newComments - это массив комментариев для каждого элемента оснащения
                clearValue = { 'v': newComments };
            } else {
                // Если нет оснащения, это единичный комментарий
                clearValue = { 'v': Array.isArray(newComments) ? newComments : [newComments] };
            }

            // Обновляем комментарии в editedRows
            setEditedRows(prev => ({
                ...prev,
                [commentEditObject.id]: {
                    ...prev[commentEditObject.id],
                    changes: {
                        ...prev[commentEditObject.id]?.changes,
                        comment: clearValue
                    }
                }
            }));
        }
    };

    const handleOpenNameByActsDialog = (object) => {
        // Получаем исходный объект из data, а не из filteredData
        // чтобы избежать потери данных при применении фильтров
        const originalObject = data.find(item => item.id === object.id);
        if (!originalObject) {
            console.error('Object not found in data:', object.id);
            return;
        }

        // Применяем изменения из editedRows к исходному объекту
        const objectWithChanges = {
            ...originalObject,
            ...editedRows[originalObject.id]?.changes
        };

        setNameByActsEditObject(objectWithChanges);
        setNameByActsDialogOpen(true);
    };

    const handleCloseNameByActsDialog = () => {
        setNameByActsDialogOpen(false);
        setNameByActsEditObject(null);
    };

    const handleSaveNameByActs = (newNameByActs) => {
        if (nameByActsEditObject) {
            // Получаем исходный объект из data для правильного обновления массива
            const originalObject = data.find(item => item.id === nameByActsEditObject.id);
            if (!originalObject) {
                console.error('Object not found in data:', nameByActsEditObject.id);
                return;
            }

            // Получаем текущее состояние объекта с учетом изменений из editedRows
            const existingRow = {
                ...originalObject,
                ...editedRows[nameByActsEditObject.id]?.changes
            };

            // Определяем, нужно ли обновлять массив или это единичное значение
            const equipmentArray = existingRow.equipment?.v || [];
            let clearValue;

            if (equipmentArray.length > 0) {
                // Если есть оснащение, обновляем массив
                // newNameByActs - это массив для каждого элемента оснащения
                clearValue = { 'v': newNameByActs };
            } else {
                // Если нет оснащения, это единичное значение
                clearValue = { 'v': Array.isArray(newNameByActs) ? newNameByActs : [newNameByActs] };
            }

            // Обновляем name_by_acts в editedRows
            setEditedRows(prev => ({
                ...prev,
                [nameByActsEditObject.id]: {
                    ...prev[nameByActsEditObject.id],
                    changes: {
                        ...prev[nameByActsEditObject.id]?.changes,
                        name_by_acts: clearValue
                    }
                }
            }));
        }
    };

    const handleDeleteObject = async (object) => {
        if (window.confirm(`Вы уверены, что хотите удалить объект "${object.id}"?`)) {
            try {
                const response = await $authHost.delete(`/apig/reestr/${object.id}`, {
                    data: {
                        modifier: user.id
                    }
                });
                const { success } = response.data;

                if (success) {
                    // Обновить данные - убираем удаленный объект из состояния
                    setData(prevData => prevData.filter(item => item.id !== object.id));
                    // alert('Объект успешно удален');
                } else {
                    throw new Error(response.error || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Error deleting object:', error);
                alert(`Ошибка при удалении: ${error.message}`);
            }
        }
    };

    // Функции для работы с выбранными строками
    const handleRowSelection = (rowId, checked) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(rowId);
            } else {
                newSet.delete(rowId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(new Set(filteredData.map(row => row.id)));
        } else {
            setSelectedRows(new Set());
        }
    };
    const handleBulkDelete = async () => {
        if (selectedRows.size === 0) return;

        const selectedObjects = filteredData.filter(row => selectedRows.has(row.id));
        const objectNames = selectedObjects.map(obj => obj.id).join(', ');

        if (window.confirm(`Вы уверены, что хотите удалить ${selectedRows.size} объектов (${objectNames})?`)) {
            try {
                setLoading(true);

                // Удаляем объекты по одному
                const deletePromises = selectedObjects.map(async (object) => {
                    const response = await $authHost.delete(`/apig/reestr/${object.id}`, {
                        data: {
                            modifier: user.id
                        }
                    });
                    return { id: object.id, success: response.data.success };
                });

                const results = await Promise.all(deletePromises);
                const failedDeletes = results.filter(result => !result.success);

                if (failedDeletes.length === 0) {
                    // Обновляем данные - убираем удаленные объекты
                    setData(prevData => prevData.filter(item => !selectedRows.has(item.id)));
                    setSelectedRows(new Set());
                    alert(`Успешно удалено ${selectedRows.size} объектов`);
                } else {
                    alert(`Ошибка при удалении ${failedDeletes.length} объектов`);
                }
            } catch (error) {
                console.error('Error bulk deleting objects:', error);
                alert(`Ошибка при массовом удалении: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCancelChanges = () => {
        // Восстанавливаем оригинальные данные
        // setData(prev => {
        //     let newData = [...prev];
        //     editedRows.forEach(editedRow => {
        //         const index = newData.findIndex(item => item.id === editedRow.id);
        //         if (index !== -1) {
        //             newData[index] = { ...newData[index], ...editedRow.originalData };
        //         }
        //     });
        //     return newData;
        // });

        // Очищаем editedRows
        setEditedRows({});
    };

    const handleSaveChanges = async () => {
        console.log('Save changes:', editedRows)
        try {
            // Отправляем изменения на сервер
            const response = await $authHost.put('/apig/reestr/batch', {
                updates: Object.entries(editedRows).map(([id, row]) => ({
                    id: id,
                    changes: row.changes,
                    modifier: user.id
                }))
            });

            if (response.data.success) {
                // Очищаем editedRows после успешного сохранения
                setEditedRows({});
                // Обновляем данные с сервера
                fetchData();
            } else {
                throw new Error(response.data.error || 'Ошибка при сохранении');
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            alert(`Ошибка при сохранении: ${error.message}`);
        }
    };


    // Обработчик сортировки
    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            // Переключаем направление сортировки
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Устанавливаем новый столбец для сортировки
            setSortBy(columnId);
            setSortDirection('asc');
        }

        // Добавляем небольшую задержку для лучшего UX
        setTimeout(() => {
            // Принудительно обновляем отфильтрованные данные
            const filtered = filterAndSortData(data, searchQuery, selectedProjects, advancedFilters, columnId, sortBy === columnId ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
            setFilteredData(filtered);
        }, 0);
    };

    // Обработчики для управления видимостью столбцов
    const handleColumnMenuOpen = (event) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnVisibilityChange = (columnId, visible) => {
        try {
            setVisibleColumns(prev => ({
                ...prev,
                [columnId]: visible
            }));
        } catch (error) {
            console.warn('Failed to change column visibility:', error);
        }
    };

    const handleShowAllColumns = () => {
        try {
            setVisibleColumns(columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}));
        } catch (error) {
            console.warn('Failed to show all columns:', error);
        }
    };

    const handleHideAllColumns = () => {
        try {
            // Оставляем хотя бы первый столбец видимым
            const newVisibility = columns.reduce((acc, col, index) => ({
                ...acc,
                [col.id]: index === 0
            }), {});
            setVisibleColumns(newVisibility);
        } catch (error) {
            console.warn('Failed to hide all columns:', error);
        }
    };

    // Получаем только видимые столбцы
    const getVisibleColumns = useCallback(() => {
        return columns.filter(col => visibleColumns[col.id]);
    }, [visibleColumns]);

    // Функция для вычисления динамической ширины столбцов
    // const getColumnWidths = useCallback(() => {
    //     const visibleCols = columns.filter(col => visibleColumns[col.id]);
    //     if (visibleCols.length === 0) return {};

    //     // Получаем ширину экрана
    //     const screenWidth = window.innerWidth;
    //     const containerPadding = 32; // Отступы контейнера
    //     const availableWidth = screenWidth - containerPadding;

    //     // Вычисляем сумму минимальных ширин
    //     const totalMinWidth = visibleCols.reduce((sum, col) => sum + col.minWidth, 0);

    //     // Если доступная ширина больше суммы минимальных ширин, распределяем оставшееся место
    //     if (availableWidth > totalMinWidth) {
    //         const extraWidth = availableWidth - totalMinWidth;
    //         const flexibleCols = visibleCols.filter(col => !col.maxWidth); // Не растягиваем узкие столбцы
    //         const extraPerCol = flexibleCols.length > 0 ? extraWidth / flexibleCols.length : 0;

    //         return visibleCols.reduce((acc, col) => {
    //             const isFlexible = flexibleCols.includes(col);
    //             acc[col.id] = isFlexible ? col.minWidth + extraPerCol : col.minWidth;
    //             return acc;
    //         }, {});
    //     }

    //     // Если места мало, используем минимальные ширины
    //     return visibleCols.reduce((acc, col) => {
    //         acc[col.id] = col.minWidth;
    //         return acc;
    //     }, {});
    // }, [visibleColumns]);

    // Пересчитываем ширины столбцов при изменении видимости
    // useEffect(() => {
    //     setColumnWidths(getColumnWidths());
    // }, [getColumnWidths]);

    // Пересчитываем ширины при изменении размера окна
    // useEffect(() => {
    //     const handleResize = () => {
    //         setColumnWidths(getColumnWidths());
    //     };

    //     window.addEventListener('resize', handleResize);
    //     // Начальный расчет
    //     setColumnWidths(getColumnWidths());

    //     return () => {
    //         window.removeEventListener('resize', handleResize);
    //     };
    // }, [getColumnWidths]);

    // Функции для управления продвинутыми фильтрами
    const handleApplyAdvancedFilters = () => {
        // Фильтры применяются автоматически через useEffect
        setShowAdvancedFilters(false);
    };

    const handleClearAdvancedFilters = () => {
        setAdvancedFilters([]);
        storeSettings({ advancedFilters: [] });
    };

    const getActiveAdvancedFiltersCount = () => {
        return advancedFilters.length;
    };

    const addFilter = (filterId) => {
        const filter = availableFilters.find(f => f.id === filterId);
        if (filter) {
            // Создаем уникальный идентификатор для фильтра
            const uniqueId = `${filterId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newFilter = {
                id: uniqueId, // Используем уникальный ID
                originalId: filterId, // Сохраняем оригинальный ID для логики фильтрации
                label: filter.label,
                type: filter.type,
                options: filter.options,
                operator: filter.type === 'date' || filter.type === 'number' ? 'equals' :
                    filter.type === 'text' ? 'contains' : null,
                value: filter.type === 'date' || filter.type === 'number' ? null :
                    filter.type === 'boolean' ? '' :
                        filter.type === 'text' ? '' :
                            filter.type === 'multi-select' ? [] : [],
                value2: filter.type === 'date' ? null : null, // Для фильтра "между"
                logic: filter.type === 'multi-select' ? 'or' : null,
                enabled: true
            };
            const newFilters = [...advancedFilters, newFilter];
            setAdvancedFilters(newFilters);
            storeSettings({ advancedFilters: newFilters });
        }
    };

    const removeFilter = (filterId) => {
        const newFilters = advancedFilters.filter(f => f.id !== filterId);
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters });
    };

    const updateFilter = (filterId, updates) => {
        const newFilters = advancedFilters.map(f =>
            f.id === filterId ? { ...f, ...updates } : f
        );
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters });
    };

    const getAvailableFiltersForSelect = () => {
        // Разрешаем добавлять несколько одинаковых фильтров
        return availableFilters;
    };

    // Функции для управления меню экспорта
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    const handleExportFormatSelect = (format) => {
        handleExportMenuClose();
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };
    // Функция экспорта в Excel с использованием ExcelJS
    const exportToExcel = async () => {
        try {
            // Группируем данные по проектам
            const groupedByProjects = {};
            filteredData.forEach(item => {
                const projectId = item.project_id;
                const project = projects.find(p => p.id === projectId);
                const projectName = project?.name || 'Без проекта';

                if (!groupedByProjects[projectName]) {
                    groupedByProjects[projectName] = [];
                }
                groupedByProjects[projectName].push(item);
            });

            // Сортируем группы по названию проекта
            const sortedGroups = Object.keys(groupedByProjects).sort();

            // Создаем новую рабочую книгу ExcelJS
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'GIS система';
            workbook.lastModifiedBy = 'GIS система';
            workbook.created = new Date();
            workbook.modified = new Date();
            workbook.title = 'Реестр объектов строительства';
            workbook.subject = 'Экспорт данных реестра объектов';
            workbook.keywords = 'реестр, объекты, строительство, экспорт';

            // Создаем лист
            const worksheet = workbook.addWorksheet('Реестр объектов');

            // Закрепляем первую строку (общие заголовки)
            worksheet.views = [
                { state: 'frozen', ySplit: 1 }
            ];


            // Определяем заголовки
            const headers = [
                '№', 'Наименование', 'Наименование по актам', 'Населённый пункт', 'Этап', 'Закрытие без НДС', 'Стоимость без НДС', 'Комментарий', 'Ответственный',
                'Соисполнители', 'Оснащение', 'Плановое начало', 'Плановое завершение',
                'Фактическое начало', 'Фактическое завершение', 'Создатель', 'Дата создания',
                'Редактор', 'Дата изменения'
            ];

            // Собираем все данные в один массив
            let allRows = [];
            let projectRows = []

            // Добавляем данные всех проектов
            sortedGroups.forEach((projectName, projectIndex) => {
                const projectData = groupedByProjects[projectName];

                // Добавляем строку проекта
                const projectRow = [projectName];
                allRows.push(projectRow);
                projectRows.push(allRows.length + 1)

                const tableRows = []

                // Добавляем данные объектов проекта
                projectData.forEach(item => {
                    const equip = item.equipment.v.length === 0 ? [0] : item.equipment.v
                    equip.forEach((sub, index) => {
                        tableRows.push([
                            item.fid || '',
                            transformers.get(item.object_id)?.name || '',
                            Array.isArray(item.name_by_acts?.v) ? item.name_by_acts.v[index] : (item.name_by_acts?.v?.[0] || ''),
                            transformers.get(item.object_id)?.town || '',
                            Array.isArray(item.stage.v) ? item.stage.v[index] : item.stage.v,
                            Array.isArray(item.closing.v) ? item.closing.v[index] : item.closing.v,
                            Array.isArray(item.cost.v) ? item.cost.v[index] : item.cost.v,
                            Array.isArray(item.comment.v) ? item.comment.v[index] : item.comment.v,
                            responsible[Array.isArray(item.responsible.v) ? item.responsible.v[index] : item.responsible.v] || '',
                            (Array.isArray(item.coexecutors.v) && Array.isArray(item.coexecutors.v[0]) ? item.coexecutors.v[index] : item.coexecutors.v).filter(f => f).map(coex => responsible[coex]).filter(f => f).join(', '),
                            sub === 0 && equip.length === 0 ? '' : equipment[sub],
                            formatDate(Array.isArray(item.plan_start.v) ? item.plan_start.v[index] : item.plan_start.v, true),
                            formatDate(Array.isArray(item.plan_end.v) ? item.plan_end.v[index] : item.plan_end.v, true),
                            formatDate(Array.isArray(item.fact_start.v) ? item.fact_start.v[index] : item.fact_start.v, true),
                            formatDate(Array.isArray(item.fact_end.v) ? item.fact_end.v[index] : item.fact_end.v, true),
                            responsible[item.creator] || '',
                            item.created_at ? formatDate(item.created_at, true) : '',
                            responsible[item.modifier] || '',
                            item.modified_at ? formatDate(item.modified_at, true) : ''
                        ])
                    })
                });

                allRows.push(...tableRows);
            });

            // Добавляем одну большую таблицу со всеми данными
            const endRow = allRows.length + 1; // +1 для заголовков
            worksheet.addTable({
                name: 'ObjectsReestrTable',
                ref: `A1:S${endRow}`,
                headerRow: true,
                totalsRow: false,
                style: { theme: 'TableStyleLight2', showRowStripes: true },
                columns: headers.map(h => ({ name: h, filterButton: true })),
                rows: allRows
            });
            const colWidths = [8, 20, 18, 12, 12, 12, 25, 25, 25, 25, 15, 18, 15, 18, 20, 15, 20, 15]
            const centerCols = [1, 3, 4, 5, 6, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18]


            // Устанавливаем ширину столбцов
            colWidths.forEach((width, index) => {
                const column = worksheet.getColumn(index + 1);
                column.width = width;
            });

            const dateCols = [8, 9, 10, 11, 13, 15]
            dateCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = 'dd/mm/yyyy'
            })

            const numberCols = [1, 4]
            numberCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = '0'
            })

            const currencyCols = [5, 6]
            currencyCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = '# ##0.00'
            })

            const textCols = [2, 3, 7, 8, 9, 10, 15]
            textCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = ''
            })

            // Устанавливаем черный цвет текста для всех ячеек таблицы
            for (let rowNumber = 1; rowNumber <= endRow; rowNumber++) {
                const row = worksheet.getRow(rowNumber);
                // row.height = undefined

                row.eachCell((cell, colNumber) => {
                    cell.font = {
                        ...cell.font,
                        name: 'Arial',
                        size: projectRows.includes(rowNumber) || rowNumber === 1 ? 11 : 10,
                        bold: projectRows.includes(rowNumber) || rowNumber === 1,
                        color: { argb: 'FF000000' } // Черный цвет

                    };
                    cell.alignment = {
                        horizontal: !projectRows.includes(rowNumber) && (rowNumber === 1 || centerCols.includes(colNumber)) ? 'center' : 'left',
                        vertical: 'middle',
                        wrapText: !projectRows.includes(rowNumber) || rowNumber == 1 ? true : false
                    }
                    // cell.numFmt = dateCols.includes(colNumber) ? 'dd.mm.yyyy' : ''

                    // cell.effectiveType = dateCols.includes(colNumber) ? 4 : 0


                });
                row.height = undefined; // Это включает автоподбор

                if (typeof row.commit === 'function') row.commit();
            }





            // });

            // Настраиваем ширину столбцов
            // worksheet.columns = [
            //     { key: '№', width: 8, alignment: { horizontal: 'center' } },
            //     { key: 'Наименование', width: 20 },
            //     { key: 'Населённый пункт', width: 18 },
            //     { key: 'Этап', width: 12, alignment: { horizontal: 'center' } },
            //     { key: 'Ответственный', width: 25 },
            //     { key: 'Соисполнители', width: 25 },
            //     { key: 'Оснащение', width: 25 },
            //     { key: 'Плановое начало', width: 15, alignment: { horizontal: 'center' } },
            //     { key: 'Плановое завершение', width: 18, alignment: { horizontal: 'center' } },
            //     { key: 'Фактическое начало', width: 15, alignment: { horizontal: 'center' } },
            //     { key: 'Фактическое завершение', width: 18, alignment: { horizontal: 'center' } },
            //     { key: 'Создатель', width: 20 },
            //     { key: 'Дата создания', width: 15, alignment: { horizontal: 'center' } },
            //     { key: 'Редактор', width: 20 },
            //     { key: 'Дата изменения', width: 15, alignment: { horizontal: 'center' } }
            // ];

            // Автофильтр теперь встроен в таблицу

            // Включаем автоподбор высоты строк
            // worksheet.properties.defaultRowHeight = 15;
            // worksheet.properties.defaultColWidth = 10;

            // Применяем автоподбор высоты для всех строк с данными
            // const totalRows = allRows.length + 1; // +1 для заголовков
            // for (let rowNumber = 1; rowNumber <= totalRows; rowNumber++) {
            //     const row = worksheet.getRow(rowNumber);
            //     // Устанавливаем автоподбор высоты для каждой строки
            //     row.height = undefined; // Это включает автоподбор
            //     if (typeof row.commit === 'function') row.commit();
            // }

            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Реестр_объектов_${dateStr}.xlsx`;

            // Экспортируем файл
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);

            console.log('Excel файл успешно создан с примененными стилями (ExcelJS)');
        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };

    // Функция экспорта в PDF
    const exportToPDF = () => {
        try {
            // Группируем данные по проектам
            const groupedByProjects = {};
            filteredData.forEach(item => {
                const projectId = item.project_id;
                const project = projects.find(p => p.id === projectId);
                const projectName = project?.name || 'Без проекта';

                if (!groupedByProjects[projectName]) {
                    groupedByProjects[projectName] = [];
                }
                groupedByProjects[projectName].push(item);
            });

            // Сортируем группы по названию проекта
            const sortedGroups = Object.keys(groupedByProjects).sort();

            // Создаем PDF документ
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            pdf.setFont("arial", "normal");
            pdf.setLanguage('ru')

            // Настройки страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - 2 * margin;
            const contentHeight = pageHeight - 2 * margin;

            // Заголовок документа
            pdf.setFontSize(16);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(25, 118, 210);
            pdf.text('Реестр объектов строительства', pageWidth / 2, 10, { align: 'center' });

            // Информация о документе
            pdf.setFontSize(10);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(100, 100, 100);
            const currentDate = new Date().toLocaleDateString('ru-RU');
            pdf.text(`Дата экспорта: ${currentDate}`, pageWidth - margin, 15, { align: 'right' });
            pdf.text(`Всего объектов: ${filteredData.length}`, margin, 15);

            let currentY = 20;
            let pageNumber = 1;
            let totalPages = 1; // Будет обновлено после создания всех страниц

            // Определяем заголовки таблицы (как в Excel)
            const headers = [
                '№', 'Наименование', 'Наименование по актам', 'Населённый пункт', 'Этап', 'Закрытие без НДС', 'Стоимость без НДС', 'Комментарий', 'Ответственный',
                'Соисполнители', 'Оснащение', 'Плановое начало', 'Плановое завершение',
                'Фактическое начало', 'Фактическое завершение', 'Создатель', 'Дата создания',
                'Редактор', 'Дата изменения'
            ];

            // Добавляем данные по группам
            sortedGroups.forEach((projectName, projectIndex) => {
                const projectData = groupedByProjects[projectName];

                // Проверяем, нужно ли добавить новую страницу
                if (currentY > pageHeight - 80) {
                    pdf.addPage();
                    pageNumber++;
                    currentY = 10;

                    // Начинаем с верхней части новой страницы
                    currentY = 10;
                }

                // Заголовок проекта (как в Excel - с заливкой)
                pdf.setFontSize(11);
                pdf.setFont("arial", "bold");
                pdf.setTextColor(0, 0, 0);

                // Рисуем фон для заголовка проекта (светло-голубой как в Excel)
                pdf.setFillColor(230, 243, 255); // E6F3FF
                pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 8, 'F');

                // Рисуем границы
                pdf.setDrawColor(208, 215, 224); // D0D7E0
                pdf.setLineWidth(0.1);
                pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 8);

                pdf.text(projectName, pageWidth / 2, currentY + 2, { align: 'center' });
                currentY += 12;

                // Подготавливаем данные для таблицы (как в Excel)
                // const tableData = projectData.map((item, index) => [
                //     item.fid || '',
                //     item.object?.name || '',
                //     item.object?.town || '',
                //     item.stage || '',
                //     item.closing || '',
                //     item.cost || '',
                //     item.comment || '',
                //     Array.isArray(item.responsible)
                //         ? item.responsible.map(r => r.name).join('\n')
                //         : item.responsible?.name || '',
                //     Array.isArray(item.coexecutors)
                //         ? item.coexecutors.map(c => c.name).join('\n')
                //         : '',
                //     Array.isArray(item.equipment)
                //         ? item.equipment.map(e => e.name).join('\n')
                //         : '',
                //     item.plan_start ? formatDate(item.plan_start, true) : '',
                //     item.plan_end ? formatDate(item.plan_end, true) : '',
                //     item.fact_start ? formatDate(item.fact_start, true) : '',
                //     item.fact_end ? formatDate(item.fact_end, true) : '',
                //     item.creator?.name || '',
                //     item.created_at ? formatDate(item.created_at, true) : '',
                //     item.modifier?.name || '',
                //     item.modified_at ? formatDate(item.modified_at, true) : ''
                // ]);

                const tableData = []

                // Добавляем данные объектов проекта
                projectData.forEach(item => {
                    const equip = item.equipment.v.length === 0 ? [0] : item.equipment.v
                    equip.forEach((sub, index) => {
                        tableData.push([
                            item.fid || '',
                            transformers.get(item.object_id)?.name || '',
                            Array.isArray(item.name_by_acts?.v) ? item.name_by_acts.v[index] : (item.name_by_acts?.v?.[0] || ''),
                            transformers.get(item.object_id)?.town || '',
                            Array.isArray(item.stage.v) ? item.stage.v[index] : item.stage.v,
                            Array.isArray(item.closing.v) ? item.closing.v[index] : item.closing.v,
                            Array.isArray(item.cost.v) ? item.cost.v[index] : item.cost.v,
                            Array.isArray(item.comment.v) ? item.comment.v[index] : item.comment.v,
                            responsible[Array.isArray(item.responsible.v) ? item.responsible.v[index] : item.responsible.v] || '',
                            (Array.isArray(item.coexecutors.v) && Array.isArray(item.coexecutors.v[0]) ? item.coexecutors.v[index] : item.coexecutors.v).filter(f => f).map(coex => responsible[coex]).filter(f => f).join(', '),
                            sub === 0 && equip.length === 0 ? '' : equipment[sub],
                            formatDate(Array.isArray(item.plan_start.v) ? item.plan_start.v[index] : item.plan_start.v, true),
                            formatDate(Array.isArray(item.plan_end.v) ? item.plan_end.v[index] : item.plan_end.v, true),
                            formatDate(Array.isArray(item.fact_start.v) ? item.fact_start.v[index] : item.fact_start.v, true),
                            formatDate(Array.isArray(item.fact_end.v) ? item.fact_end.v[index] : item.fact_end.v, true),
                            responsible[item.creator] || '',
                            item.created_at ? formatDate(item.created_at, true) : '',
                            responsible[item.modifier] || '',
                            item.modified_at ? formatDate(item.modified_at, true) : ''
                        ])
                    })
                });

                // Добавляем таблицу с улучшенным форматированием
                autoTable(pdf, {
                    head: [headers],
                    body: tableData,
                    startY: currentY,
                    margin: { left: margin, right: margin },
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    styles: {
                        fontSize: 8,
                        cellPadding: 1,
                        lineColor: [208, 215, 224], // D0D7E0 - как в Excel
                        lineWidth: 0.1,
                        textColor: [0, 0, 0],
                        font: 'arial',
                        fontStyle: 'normal'
                    },
                    headStyles: {
                        fillColor: [25, 118, 210], // Синий заголовок как в Excel
                        textColor: [255, 255, 255],
                        font: 'arial',
                        fontStyle: 'bold',
                        fontSize: 9,
                        cellPadding: 1,
                        halign: 'center',
                        valign: 'middle'
                    },
                    alternateRowStyles: {
                        fillColor: [230, 243, 255] // E6F3FF - светло-голубой для четных строк
                    },
                    columnStyles: {
                        0: { cellWidth: 8, halign: 'center' },   // №
                        1: { cellWidth: 25 },  // Наименование
                        2: { cellWidth: 15, halign: 'center' },  // Населённый пункт
                        3: { cellWidth: 10, halign: 'center' },  // Этап
                        4: { cellWidth: 10, halign: 'center' },  // Закрытие
                        5: { cellWidth: 10, halign: 'center' },  // Стоимость
                        6: { cellWidth: 20 },  // Комментарий
                        7: { cellWidth: 18 },  // Ответственный
                        8: { cellWidth: 22 },  // Соисполнители
                        9: { cellWidth: 25 },  // Оснащение
                        10: { cellWidth: 15, halign: 'center' }, // Плановое начало
                        11: { cellWidth: 15, halign: 'center' }, // Плановое завершение
                        12: { cellWidth: 15, halign: 'center' }, // Фактическое начало
                        13: { cellWidth: 15, halign: 'center' }, // Фактическое завершение
                        14: { cellWidth: 15 }, // Создатель
                        15: { cellWidth: 15, halign: 'center' }, // Дата создания
                        16: { cellWidth: 15 }, // Редактор
                        17: { cellWidth: 15, halign: 'center' }, // Дата изменения
                    },
                    didDrawPage: function (data) {
                        // Функция будет вызвана для каждой страницы, но номера добавим позже
                    }
                });

                // Получаем позицию после таблицы
                const tableEndY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY : currentY + 50;
                currentY = tableEndY + 5;

                // Не добавляем разделитель между проектами (как в Excel)
            });

            // Получаем общее количество страниц
            totalPages = pdf.getNumberOfPages();

            // Добавляем номера страниц на все страницы
            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                pdf.setPage(pageNum);

                // Добавляем номер страницы внизу
                pdf.setFontSize(8);
                pdf.setFont("arial", "normal");
                pdf.setTextColor(100, 100, 100);
                pdf.text(`Стр. ${pageNum} из ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
            }

            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Реестр_объектов_${dateStr}.pdf`;

            // Сохраняем PDF
            pdf.save(fileName);
            console.log('PDF файл успешно создан с форматированием как в Excel');
        } catch (error) {
            console.error('Ошибка при экспорте в PDF:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };

    return (
        <Box sx={{
            bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',

            p: 0,
            m: 0,
            display: 'flex',
            flex: 1,
            height: '100%',
            flexDirection: 'column',
            // overflow: 'hidden',
            width: '100%'

        }}>
            <Paper sx={{
                display: 'flex',
                flexDirection: 'column',
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                borderRadius: 0,
                boxShadow: 'none',
                overflow: 'hidden',
                border: 'none',
                width: '100%',
                minHeight: '100%',
            }}>
                {/* Первая строка - заголовок, поиск, темная тема */}
                <Box sx={{
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    p: { xs: 0.5, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: { xs: 1, md: 2 },
                }}>
                    {/* Левая часть - заголовок */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                        {/* <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            border: darkMode ? '2px solid #64b5f6' : '2px solid #1976d2'
                        }}>
                            <BusinessIcon sx={{
                                fontSize: '28px',
                                color: darkMode ? '#64b5f6' : '#1976d2'
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                color: darkMode ? '#f5f7fa' : '#333',
                                mb: 0.5,
                            }}>
                                Реестр объектов
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.875rem'
                            }}>
                                Управление объектами строительства
                            </Typography>
                        </Box> */}
                        {/* KDP селект */}
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: 'auto', md: '300px' },
                                flex: { xs: 1, sm: 'none' },
                                '& .MuiInputLabel-root': {
                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                    '&.Mui-focused': {
                                        color: '#1976d2'
                                    }
                                },
                            }}
                        >
                            {/* <InputLabel>Проект</InputLabel> */}
                            <Select
                                multiple
                                value={selectedProjects}
                                onChange={(e) => {
                                    const { target: { value } } = e;
                                    if (value.includes(0)) {
                                        if (selectedProjects.length === activeProjects.length) {
                                            setSelectedProjects([]);
                                        } else {
                                            setSelectedProjects(activeProjects.map(project => project.id));
                                        }
                                    } else {
                                        setSelectedProjects(
                                            typeof value === 'string' ? value.split(',').map(Number) : value,
                                        );
                                    }
                                }}
                                // label="Проект"
                                displayEmpty
                                // placeholder="Не выбрано"
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return "Выбор проектов";
                                    }
                                    if (selected.length === activeProjects.length) {
                                        return "Все проекты"
                                    }
                                    if (selected.length === 1) {
                                        const project = activeProjects.find(project => project.id === selected[0]);
                                        return project ? project.name : `Выбрано: ${selected.length}`;
                                    }
                                    return `Выбрано: ${selected.length}`;
                                }}
                                sx={{
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1976d2'
                                    },
                                    '& .MuiSelect-icon': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        '&.Mui-focused': {
                                            color: '#1976d2'
                                        },
                                        '&.MuiInputLabel-shrink': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        }
                                    },
                                    '& .MuiSelect-select': {
                                        '&:empty::before': {
                                            content: 'attr(placeholder)',
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        }
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {

                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                            '& .MuiMenuItem-root': {

                                                color: darkMode ? '#f5f7fa' : '#333',
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                }
                                            },
                                            '& .MuiTypography-root': {
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                            },
                                            '& .MuiButtonBase-root': {
                                                minHeight: 0,
                                                padding: '4px'
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value={0}>
                                    <Checkbox
                                        size="small"
                                        checked={selectedProjects.length === activeProjects.length && activeProjects.length > 0}
                                        sx={{
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                            '&.Mui-checked': {
                                                color: darkMode ? '#64b5f6' : '#1976d2'
                                            }
                                        }}
                                    />
                                    <ListItemText primary="Все проекты" />
                                </MenuItem>
                                {activeProjects.map((project) => (
                                    <MenuItem key={project.id} value={project.id}>
                                        <Checkbox
                                            size="small"
                                            checked={selectedProjects.includes(project.id)}
                                            sx={{
                                                color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                                '&.Mui-checked': {
                                                    color: darkMode ? '#64b5f6' : '#1976d2'
                                                }
                                            }}
                                        />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Центральная часть - поиск и KDP селект */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2,
                        maxWidth: { xs: '100%', md: '600px' },
                        minWidth: { xs: '100%', sm: '400px', md: '500px' },
                        order: { xs: 3, md: 0 }
                    }}>


                        {/* Поиск */}
                        <Box sx={{
                            flex: 1,
                            position: 'relative'
                        }}>
                            <TextField
                                size="small"
                                placeholder="Поиск объектов..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: <SearchIcon sx={{
                                            mr: 1,
                                            color: darkMode ? '#bbb' : 'text.secondary',
                                            fontSize: { xs: '16px', md: '20px' }
                                        }} />,
                                        endAdornment: searchQuery && (
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchQuery('')}
                                                sx={{
                                                    color: darkMode ? '#bbb' : '#666',
                                                    '&:hover': {
                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                    }
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: darkMode ? '#1e1e1e' : '#f8fafc',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&::placeholder': {
                                            color: darkMode ? '#bbb' : '#666',
                                            opacity: 1
                                        }
                                    }
                                }}
                            />
                            {/* Индикатор результатов поиска */}
                            {/* {searchQuery && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        mt: 0.5,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    Найдено: {filteredData.length} из {data.length}
                                </Typography>
                            )} */}
                        </Box>
                        {/* Правая часть - кнопки управления */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* Кнопка скрытия меню кнопок управления */}
                            <Tooltip title={showControlMenu ? "Скрыть меню кнопок управления" : "Показать меню кнопок управления"}>
                                <IconButton
                                    onClick={() => setShowControlMenu(!showControlMenu)}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#666',
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {showControlMenu ?
                                        <VisibilityOffIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} /> :
                                        <VisibilityIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
                                    }
                                </IconButton>
                            </Tooltip>

                            {/* Кнопка настроек столбцов */}
                            <Tooltip title="Настроить видимость столбцов">
                                <IconButton
                                    onClick={handleColumnMenuOpen}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#666',
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <ViewColumnIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
                                </IconButton>
                            </Tooltip>

                            {/* Кнопка темной темы */}
                            {/* <IconButton
                                onClick={() => dispatch(toggleDarkMode())}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#666',
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {darkMode ? <LightModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} /> : <DarkModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />}
                            </IconButton> */}
                        </Box>
                    </Box>


                </Box>

                {/* Вторая строка - панель инструментов */}
                {showControlMenu && (
                    <Box sx={{
                        p: { xs: 1, md: 1 },
                        borderBottom: darkMode
                            ? '1px solid rgba(255, 255, 255, 0.12)'
                            : '1px solid rgba(0, 0, 0, 0.12)',
                        bgcolor: darkMode ? '#252525' : '#f8fafc',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 1, md: 2 },
                    }}>
                        {/* Индикатор активных фильтров */}
                        {/* {(searchQuery || filterMode !== 'all' || sortMode !== 'created_desc' || selectedKdp.length > 0) && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            p: 1,
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                            borderRadius: 1,
                            border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                        }}>
                            <Typography variant="caption" sx={{ color: darkMode ? '#64b5f6' : '#1976d2', fontWeight: 600 }}>
                                Активные фильтры:
                            </Typography>
                            {searchQuery && (
                                <Chip
                                    label={`Поиск: "${searchQuery}"`}
                                    size="small"
                                    onDelete={() => setSearchQuery('')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {selectedKdp.length > 0 && (
                                <Chip
                                    label={`KDP: ${selectedKdp.length} выбрано`}
                                    size="small"
                                    onDelete={() => setSelectedKdp([])}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {filterMode !== 'all' && (
                                <Chip
                                    label={`Фильтр: ${filterMode === 'my' ? 'Мои объекты' : 'Активные этапы'}`}
                                    size="small"
                                    onDelete={() => setFilterMode('all')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {sortMode !== 'created_desc' && (
                                <Chip
                                    label={`Сортировка: ${sortMode}`}
                                    size="small"
                                    onDelete={() => setSortMode('created_desc')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                        color: darkMode ? '#81c784' : '#388e3c',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                        </Box>
                    )} */}

                        {/* Основные элементы управления */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 1, md: 2 },
                            alignItems: { xs: 'stretch', md: 'center' },
                            justifyContent: 'space-between'
                        }}>
                            {/* Левая группа - кнопка создания и фильтры */}
                            <Box sx={{
                                display: 'flex',
                                gap: { xs: 1, md: 2 },
                                flex: 1,
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap',
                                alignItems: { xs: 'stretch', sm: 'center' }
                            }}>
                                {/* Кнопка создания */}
                                {user.roles?.includes('project_manager') && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenCreateDialog()}
                                        sx={{
                                            bgcolor: '#4caf50',
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                            boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                            '&:hover': {
                                                bgcolor: '#388e3c',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Создать объект
                                    </Button>
                                )}
                                {/* Кнопка продвинутых фильтров */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<FilterIcon />}
                                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                    sx={{
                                        borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        px: 1,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: 1,
                                        minWidth: 'auto',
                                        '&:hover': {
                                            borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                            transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.2s ease',
                                        ...(getActiveAdvancedFiltersCount() > 0 && {
                                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                            borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                        })
                                    }}
                                >
                                    Фильтры
                                    {getActiveAdvancedFiltersCount() > 0 && (
                                        <Chip
                                            label={getActiveAdvancedFiltersCount()}
                                            size="small"
                                            sx={{
                                                ml: 0.5,
                                                bgcolor: darkMode ? '#1976d2' : '#1976d2',
                                                color: '#fff',
                                                fontSize: '0.7rem',
                                                height: '16px',
                                                minWidth: '16px'
                                            }}
                                        />
                                    )}
                                </Button>
                                {/* Объединенная кнопка экспорта с меню */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<FileDownloadIcon />}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    onClick={handleExportMenuOpen}
                                    disabled={filteredData.length === 0}
                                    sx={{
                                        borderColor: darkMode ? '#1976d2' : '#1976d2',
                                        color: darkMode ? '#1976d2' : '#1976d2',
                                        px: 1,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: 1,
                                        minWidth: 'auto',
                                        '&:hover': {
                                            borderColor: darkMode ? '#1565c0' : '#1565c0',
                                            bgcolor: darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:disabled': {
                                            borderColor: darkMode ? '#666' : '#ccc',
                                            color: darkMode ? '#666' : '#ccc',
                                            transform: 'none',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Экспорт
                                </Button>
                                {/* Меню выбора формата экспорта */}
                                <Menu
                                    anchorEl={exportMenuAnchor}
                                    open={Boolean(exportMenuAnchor)}
                                    onClose={handleExportMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                    sx={{
                                        '& .MuiPaper-root': {
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                            boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                        }
                                    }}
                                >
                                    <MenuItem
                                        onClick={() => handleExportFormatSelect('excel')}
                                        sx={{
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                            }
                                        }}
                                    >
                                        <FileDownloadIcon sx={{ mr: 1, color: '#4caf50' }} />
                                        Экспорт в Excel
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => handleExportFormatSelect('pdf')}
                                        sx={{
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                            }
                                        }}
                                    >
                                        <PictureAsPdfIcon sx={{ mr: 1, color: '#f44336' }} />
                                        Экспорт в PDF
                                    </MenuItem>
                                </Menu>
                                {/* Фильтр */}
                                {/* <FormControl
                                size="small"
                                sx={{
                                    minWidth: { xs: 'auto', md: '150px' },
                                    flex: { xs: 1, sm: 'none' },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        '&.Mui-focused': {
                                            color: '#1976d2'
                                        }
                                    }
                                }}
                            >
                                <InputLabel>Фильтр</InputLabel>
                                <Select
                                    value={filterMode}
                                    size='small'
                                    onChange={(e) => setFilterMode(e.target.value)}
                                    label="Фильтр"
                                    startAdornment={<FilterIcon sx={{
                                        mr: 1,
                                        color: darkMode ? '#bbb' : 'text.secondary',
                                        fontSize: '18px'
                                    }} />}
                                    sx={{
                                        fontSize: '0.75rem',
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.7rem'
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                                border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.75rem',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Все объекты ({data.length})</MenuItem>
                                   
                                </Select>
                            </FormControl> */}

                                {/* Кнопка сброса фильтров */}
                                {/* {(searchQuery || filterMode !== 'all' || selectedKdp.length > 0) && (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setFilterMode('all');
                                        setSelectedKdp([]);
                                    }}
                                    sx={{
                                        mb: 1,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        '&:hover': {
                                            color: darkMode ? '#f5f7fa' : '#333',
                                        }
                                    }}
                                >
                                    Сбросить фильтры
                                </Button>
                            )} */}
                            </Box>

                            {/* Правая группа - кнопка редактирования */}
                            <Box sx={{
                                display: 'flex',
                                gap: { xs: 1, md: 2 },
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'stretch', md: 'center' },
                                justifyContent: 'space-between'
                            }}>
                                {/* Индикатор режима редактирования */}
                                {/* {editMode && (
                                <Chip
                                    label="Режим редактирования"
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                    }}
                                />
                            )} */}

                                {/* Индикатор изменений */}
                                {/* {editedRows.length > 0 && (
                                <Chip
                                    label={`${editedRows.length} изменений`}
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                                    }}
                                />
                            )} */}

                                {/* Кнопки отмены и сохранения */}
                                {Object.keys(editedRows).length > 0 && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: { xs: 1, md: 2 },
                                        alignItems: { xs: 'stretch', md: 'center' },
                                        justifyContent: 'center'
                                    }}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={handleCancelChanges}
                                            sx={{
                                                borderColor: darkMode ? '#f44336' : '#d32f2f',
                                                color: darkMode ? '#f44336' : '#d32f2f',
                                                px: 1.5,
                                                py: 0.5,
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                borderRadius: 1.5,
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                                    borderColor: darkMode ? '#f44336' : '#d32f2f',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            Отменить
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSaveChanges}
                                            sx={{
                                                bgcolor: '#4caf50',
                                                color: 'white',
                                                px: 1.5,
                                                py: 0.5,
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                borderRadius: 1.5,
                                                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                                '&:hover': {
                                                    bgcolor: '#388e3c',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            Сохранить
                                        </Button>
                                    </Box>
                                )}

                                {/* Кнопка редактирования */}
                                {user.roles?.includes('project_manager') && (
                                    <Button
                                        variant={editMode ? "contained" : "outlined"}
                                        startIcon={<EditIcon />}
                                        onClick={() => {
                                            if (editMode && editedRows.length > 0) {
                                                if (window.confirm('У вас есть несохраненные изменения. Выйти из режима редактирования?')) {
                                                    setEditMode(false);
                                                    setSelectedRows(new Set());
                                                    // setEditedRows([]);
                                                    handleCancelChanges()
                                                }
                                            } else {
                                                setEditMode(!editMode);
                                                if (!editMode) {
                                                    // Входим в режим редактирования
                                                    setSelectedRows(new Set());
                                                } else {
                                                    // Выходим из режима редактирования
                                                    setSelectedRows(new Set());
                                                }
                                            }
                                        }}
                                        sx={{
                                            bgcolor: editMode ? '#1976d2' : 'transparent',
                                            color: editMode ? 'white' : (darkMode ? '#64b5f6' : '#1976d2'),
                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                            px: 1,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            boxShadow: editMode ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                                            '&:hover': {
                                                bgcolor: editMode ? '#1565c0' : (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)'),
                                                transform: editMode ? 'translateY(-1px)' : 'none',
                                                boxShadow: editMode ? '0 4px 12px rgba(25, 118, 210, 0.4)' : 'none'
                                            },
                                            transition: 'all 0.2s ease',
                                            minWidth: { xs: '100%', sm: 'auto' },
                                        }}
                                    >
                                        {editMode ? 'Завершить редактирование' : 'Редактировать'}
                                    </Button>
                                )}

                                {/* Кнопка удаления выбранных строк */}
                                {editMode && selectedRows.size > 0 && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleBulkDelete}
                                        sx={{
                                            bgcolor: '#f44336',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                            '&:hover': {
                                                bgcolor: '#d32f2f',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)'
                                            },
                                            transition: 'all 0.2s ease',
                                            minWidth: { xs: '100%', sm: 'auto' },
                                        }}
                                    >
                                        Удалить ({selectedRows.size})
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Продвинутые фильтры */}
                {showAdvancedFilters && (
                    <AdvancedFiltersComponent
                        darkMode={darkMode}
                        advancedFilters={advancedFilters}
                        setAdvancedFilters={setAdvancedFilters}
                        responsible={responsible}
                        equipment={equipment}
                        stages={stages}
                        onApplyFilters={handleApplyAdvancedFilters}
                        onClearFilters={handleClearAdvancedFilters}
                        addFilter={addFilter}
                        removeFilter={removeFilter}
                        updateFilter={updateFilter}
                        getAvailableFiltersForSelect={getAvailableFiltersForSelect}
                    />
                )}

                {/* Таблица */}
                <Box sx={{
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    width: '100%',
                }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f5f7fa' : '#333',
                            gap: 2,
                        }}>
                            <CircularProgress size={40} sx={{ color: darkMode ? '#64b5f6' : '#1976d2' }} />
                            <Typography variant="h6">Загрузка данных...</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f44336' : '#d32f2f',
                        }}>
                            <Typography variant="h6">Ошибка загрузки: {error}</Typography>
                        </Box>
                    ) : (filteredData.length === 0 && groupedData.groupedData.length === 0) ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#bbb' : '#666',
                        }}>
                            <Typography variant="h6">
                                {searchQuery || advancedFilters.length > 0
                                    ? 'По вашему запросу ничего не найдено'
                                    : 'Нет данных для отображения'
                                }
                            </Typography>
                        </Box>
                    ) : (
                        <GroupedTableVirtuoso
                            key={'grouped-table'}
                            data={groupedData.groupedData}
                            overscan={200}
                            followOutput={false}
                            // atBottomThreshold={200}
                            // atTopThreshold={200}
                            // Предварительный рендер вне вьюпорта - уменьшено для стабильности
                        
                            increaseViewportBy={{ top: 500, bottom: 500 }}
                            // Фиксированная высота строки для стабильности прокрутки при rowSpan
                            // defaultItemHeight={200}
                            minOverscanItemCount={3}
                            // Плавная деградация контента при быстром скролле
                            // scrollSeekConfiguration={{
                            //     enter: (velocity) => Math.abs(velocity) > 800,
                            //     exit: (velocity) => Math.abs(velocity) < 30,
                            //     change: (_, range) => range,
                            // }}
                            groupCounts={groupedData.groupCounts}
                            totalCount={groupedData.groupedData.length}
                            components={VirtuosoTableComponents}
                            fixedHeaderContent={() => fixedHeaderContent(darkMode, editMode, selectedRows, filteredData, handleSelectAll, sortBy, sortDirection, handleSort, visibleColumns, columnWidths, getColumnWidth, handleResizeStart)}
                            groupContent={(index) => groupContent(index, groupedData, darkMode, user, handleOpenCreateDialog, activeProjects, visibleColumns)}
                            // Ключ для правильной идентификации элементов при виртуализации
                            computeItemKey={(index) => {
                                const row = groupedData.groupedData[index];
                                if (!row) return `row-${index}`;
                                return row.isPadding ? `padding-${row.id}` : `${row.id}-${row.equipmentIndex || 0}`;
                            }}
                            itemContent={(index, groupIndex) => {
                                const row = groupedData.groupedData[index];
                                if (!row) {
                                    console.warn('Missing row at index:', index);
                                    return null;
                                }
                                const cells = rowContent(index, row, {
                                    editMode,
                                    editing,
                                    openHistoryDialog: handleOpenHistoryDialog,
                                    openCommentDialog: handleOpenCommentDialog,
                                    openNameByActsDialog: handleOpenNameByActsDialog,
                                    saveEditedField: handleEditedField,
                                    setEditingField: handleChangeEdititng,
                                    users: responsible,
                                    equipmentMap: equipment,
                                    darkMode,
                                    editedRows,
                                    selectedRows,
                                    handleRowSelection,
                                    handleOpenEditDialog,
                                    visibleColumns,
                                    handleMoveEquipment,
                                    searchQuery,
                                    advancedFilters,
                                    getColumnWidth
                                });


                                // Возвращаем ячейки как есть - фильтрация видимости столбцов 
                                // уже обрабатывается в функции rowContent через параметр visibleColumns
                                return cells;
                            }}
                            style={{
                                backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                width: '100%',
                                height: '100%',
                                minHeight: '400px',
                            }}

                        />

                    )}
                </Box>
            </Paper >

            <ObjectDialog
                open={objectDialogOpen}
                currentUser={user}
                onClose={handleCloseObjectDialog}
                objectData={selectedObject}
                darkMode={darkMode}
                responsible={Object.entries(responsible).map(([id, name]) => ({ id: Number(id), name }))}
                equipment={Object.entries(equipment).map(([id, name]) => ({ id: Number(id), name }))}
                stages={stages}
                projects={projects}
                selectedProjectId={selectedProjectId}
            />
            <AuditHistoryDialog
                open={historyDialogOpen}
                onClose={handleCloseHistoryDialog}
                tableName="objects_reestr"
                tableTitle="Реестр объектов"
                recordId={selectedObject?.id}
                recordTitle={selectedObject?.object?.name}
                darkMode={darkMode}
                translates={
                    {
                        'fid': '№',
                        'responsible': 'Ответственный',
                        'coexecutors': 'Соисполнители',
                        'equipment': 'Оснащение',
                        'stage': 'Этап',
                        'project_id': 'Проект',
                        'plan_start': 'Плановое начало',
                        'plan_end': 'Плановое завершение',
                        'fact_start': 'Фактическое начало',
                        'fact_end': 'Фактическое завершение',
                        'closing': 'Закрытие без НДС',
                        'cost': 'Стоимость без НДС',
                        'comment': 'Комментарий',
                        'name_by_acts': 'Наименование по актам'
                    }
                }
                formatting={
                    {
                        'name_by_acts': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (item && item.equipment) {
                                    return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${newValue[indx] || ''}`))
                                } else {
                                    return newValue || ''
                                }
                            }
                            return newValue || ''
                        },
                        'plan_start': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${formatDateShort(newValue[indx])}`))
                                    } else {
                                        return newValue.map((v, i) => formatDateShort(v))
                                    }
                                }
                            }
                            return formatDateShort(newValue)
                        },
                        'plan_end': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${formatDateShort(newValue[indx])}`))
                                    } else {
                                        return newValue.map((v, i) => formatDateShort(v))
                                    }
                                }
                            }
                            return formatDateShort(newValue)
                        },
                        'fact_start': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${formatDateShort(newValue[indx])}`))
                                    } else {
                                        return newValue.map((v, i) => formatDateShort(v))
                                    }
                                }
                            }
                            return formatDateShort(newValue)
                        },
                        'fact_end': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${formatDateShort(newValue[indx])}`))
                                    } else {
                                        return newValue.map((v, i) => formatDateShort(v))
                                    }
                                }
                            }
                            return formatDateShort(newValue)
                        },
                        'stage': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${newValue[indx]}`))
                                    } else {
                                        return newValue
                                    }
                                }
                            }
                            return newValue
                        },
                        'closing': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${formatRuble(newValue[indx] || 0)}`))
                                    } else {
                                        return newValue.map((v, i) => formatRuble(v || 0))
                                    }
                                }
                            }
                            return formatRuble(newValue || 0)
                        },
                        'cost': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (Array.isArray(newValue)) {
                                    if (item && item.equipment) {
                                        return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${formatRuble(newValue[indx] || 0)}`))
                                    } else {
                                        return newValue.map((v, i) => formatRuble(v || 0))
                                    }
                                }
                            }
                            return formatRuble(newValue || 0)
                        },
                        'comment': (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (item && item.equipment) {
                                    return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${newValue[indx] || ''}`))
                                } else {
                                    return newValue || ''
                                }

                            }
                            return newValue || ''
                        },
                        responsible: (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (Array.isArray(newValue)) {
                                if (item && item.equipment) {
                                    return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${newValue[indx]}`))
                                } else {
                                    return newValue.map((v, i) => responsible[v] || ' ')
                                }
                            }
                            return responsible[newValue] || ' '
                        },
                        coexecutors: (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            if (newValue && newValue[0] && Array.isArray(newValue[0])) {
                                if (item && item.equipment) {
                                    return item.equipment.v.map((id, indx) => (`${equipment[id]}: ${newValue[indx].map(u => responsible[u] || ' ').join(', ')}`))
                                } else {
                                    return newValue.map(v => `(${v.map(u => responsible[u] || ' ').join(', ')})`)
                                }
                            }
                            return newValue.map(c => responsible[c] || ' ')
                        },
                        equipment: (value, item = null) => {
                            const newValue = value.v ? value.v : value
                            return newValue.map(c => equipment[c])
                        },
                        'project_id': (value) => {
                            return projects.find(p => p.id === value)?.name
                        }
                    }
                }
                ignore={
                    ['modifier']
                }
            />

            <EquipmentTextEditDialog
                open={commentDialogOpen}
                onClose={handleCloseCommentDialog}
                onSave={handleSaveComments}
                equipment={commentEditObject?.equipment?.v?.map(id => ({
                    id,
                    name: equipment[id] || `ID: ${id}`
                })) || []}
                comments={commentEditObject?.comment?.v || []}
                darkMode={darkMode}
                objectName={commentEditObject?.object?.name || 'Объект'}
                title="Редактирование комментариев"
                equipmentLabel="Оснащение"
                textLabel="Комментарий"
                previewLabel="Превью"
                placeholder="Введите комментарий (поддерживается Markdown: **жирный**, *курсив*, # заголовок, - список)"
                generalItemLabel="Общий комментарий"
            />

            <EquipmentTextEditDialog
                open={nameByActsDialogOpen}
                onClose={handleCloseNameByActsDialog}
                onSave={handleSaveNameByActs}
                equipment={nameByActsEditObject?.equipment?.v?.map(id => ({
                    id,
                    name: equipment[id] || `ID: ${id}`
                })) || []}
                comments={nameByActsEditObject?.name_by_acts?.v || []}
                darkMode={darkMode}
                objectName={nameByActsEditObject?.object?.name || 'Объект'}
                title="Редактирование наименования по актам"
                equipmentLabel="Оснащение"
                textLabel="Наименование по актам"
                previewLabel="Превью"
                placeholder="Введите наименование по актам (поддерживается Markdown: **жирный**, *курсив*, # заголовок, - список)"
                generalItemLabel="Общее наименование по актам"
            />

            {/* Меню настройки видимости столбцов */}
            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={handleColumnMenuClose}
                PaperProps={{
                    sx: {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        minWidth: 250,
                        maxHeight: 400,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            color: darkMode ? '#f5f7fa' : '#333',
                            padding: '0',
                            pr: 1,
                            borderRadius: '6px',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Заголовок меню */}
                <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center', margin: '0 !important', padding: '0 !important', borderRadius: '0 !important' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkMode ? '#f5f7fa' : '#333' }}>
                        Видимость столбцов
                    </Typography>
                </MenuItem>

                <Divider sx={{
                    bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    margin: '4px 0'
                }} />

                {/* Кнопки управления всеми столбцами */}
                <MenuItem onClick={handleShowAllColumns} sx={{ justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: darkMode ? '#64b5f6' : '#1976d2', fontWeight: 500 }}>
                        Показать все
                    </Typography>
                </MenuItem>
                <MenuItem onClick={handleHideAllColumns} sx={{ justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: darkMode ? '#64b5f6' : '#1976d2', fontWeight: 500 }}>
                        Скрыть все
                    </Typography>
                </MenuItem>

                <Divider sx={{
                    bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    margin: '4px 0'
                }} />

                {/* Список столбцов с чекбоксами */}
                {columns.map((column) => (
                    <MenuItem
                        key={column.id}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            checked={visibleColumns[column.id] || false}
                            onChange={(e) => handleColumnVisibilityChange(column.id, e.target.checked)}
                            size="small"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                '&.Mui-checked': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                mr: 0
                            }}
                        />
                        <ListItemText
                            primary={column.label}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                color: darkMode ? '#f5f7fa' : '#333'
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </Box >
    );
};

export default ObjectsReestrView;
